import { useState, useEffect } from 'react'
import { Routes, Route, Link, Navigate, useParams, useNavigate } from 'react-router-dom'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import api from '@/services/api'
import { useAuthStore } from '@/stores/authStore'
import { CardSkeleton } from '@/components/SkeletonLoader'
import { Modal } from '@/components/Modal'
import PermissionGate from '@/components/PermissionGate'
import { useHasPermission } from '@/hooks/usePermission'
import { ROLE_LABELS, ROLE_DESCRIPTIONS } from '@/lib/permissions'
import Pagination, { paginate } from '@/components/Pagination'
import { downloadCsvResponse, parseAxiosBlobError } from '@/utils/downloadCsv'
import AdminPricingTiersPage from '@/pages/AdminPricingTiersPage'
import CurrencySection from '@/components/CurrencySection'

// ─── Shared Components ──────────────────────────────────────────────────────

function StatusBadge({ status }: { status: string }) {
  const colors: Record<string, { bg: string; color: string; border: string }> = {
    active: { bg: 'rgba(152,202,63,0.15)', color: '#1a2e44', border: '#98ca3f' },
    pending_validation: { bg: '#fff3cd', color: '#856404', border: '#ffc107' },
    suspended: { bg: '#f8d7da', color: '#721c24', border: '#f5c6cb' },
    inactive: { bg: '#e2e3e5', color: '#383d41', border: '#d6d8db' },
    revoked: { bg: '#f8d7da', color: '#721c24', border: '#f5c6cb' },
    invited: { bg: '#cce5ff', color: '#004085', border: '#b8daff' },
  }
  const c = colors[status] || colors.inactive
  return (
    <span style={{
      padding: '0.25rem 0.75rem', borderRadius: '4px', fontSize: '0.75rem',
      fontWeight: 600, textTransform: 'capitalize',
      background: c.bg, color: c.color, border: `1px solid ${c.border}`,
    }}>
      {status.replace(/_/g, ' ')}
    </span>
  )
}

function PageHeader({ title, action }: { title: string; action?: React.ReactNode }) {
  return (
    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.5rem' }}>
      <h2 style={{ margin: 0 }}>{title}</h2>
      {action}
    </div>
  )
}

function ThingsToDoCsvExportButton({
  vendorId,
  vendorUsername,
  allEligible = false,
  style,
}: {
  vendorId?: string
  vendorUsername?: string
  allEligible?: boolean
  style?: React.CSSProperties
}) {
  const { user } = useAuthStore()
  const isTixxlyAdmin = ['SUPER_ADMIN', 'ADMIN'].includes(user?.role?.toUpperCase() || '')
  const [loading, setLoading] = useState(false)
  const [statusLabel, setStatusLabel] = useState<string | null>(null)
  const [progress, setProgress] = useState<{ done: number; total: number } | null>(null)
  const [error, setError] = useState<string | null>(null)

  if (!isTixxlyAdmin) return null

  const label = allEligible
    ? 'Export Things to Do CSV (all active vendors)'
    : 'Export Things to Do CSV'

  const progressPct = progress && progress.total > 0
    ? Math.min(100, Math.round((progress.done / progress.total) * 100))
    : null

  const fallback = vendorUsername
    ? `ttd-${vendorUsername}.csv`
    : 'ttd-all-active.csv'

  const pollLiveJob = async (jobId: string) => {
    const deadline = Date.now() + 14 * 60 * 1000
    while (Date.now() < deadline) {
      setStatusLabel('Fetching live storefront prices…')
      await new Promise((r) => setTimeout(r, 2500))
      const response = await api.admin.thingsToDo.getExportJob(jobId)
      const contentType = response.headers['content-type'] as string | undefined

      if (contentType?.includes('text/csv') || contentType?.includes('application/octet-stream')) {
        setProgress((prev) => (prev?.total ? { done: prev.total, total: prev.total } : prev))
        setStatusLabel('Downloading CSV…')
        await downloadCsvResponse(response, fallback)
        setStatusLabel(null)
        setProgress(null)
        return
      }

      const text = await (response.data as Blob).text()
      let body: {
        status?: string
        error?: string | { message?: string }
        data?: {
          status?: string
          error?: string
          progressDone?: number
          progressTotal?: number
        }
        progressDone?: number
        progressTotal?: number
      }
      try {
        body = JSON.parse(text)
      } catch {
        throw new Error(text || 'Unexpected export job response')
      }
      const nested = body.data
      const status = body.status ?? nested?.status
      const done = body.progressDone ?? nested?.progressDone ?? 0
      const total = body.progressTotal ?? nested?.progressTotal ?? 0
      if (total > 0) {
        setProgress({ done, total })
        setStatusLabel(`Fetching live prices… ${done} / ${total}`)
      }

      const jobError = typeof body.error === 'string'
        ? body.error
        : body.error?.message ?? (typeof nested?.error === 'string' ? nested.error : undefined)

      if (status === 'failed') {
        throw new Error(jobError || 'Export failed')
      }
      if (status === 'ready') continue
    }
    throw new Error('Export timed out — try again or export a single vendor')
  }

  const handleExport = async () => {
    setLoading(true)
    setError(null)
    setProgress(null)
    setStatusLabel('Starting live price export…')
    try {
      const start = vendorId
        ? await api.admin.thingsToDo.startVendorExport(vendorId)
        : await api.admin.thingsToDo.startExport()
      const jobId = (start.data as { jobId?: string; data?: { jobId?: string } })?.jobId
        ?? (start.data as { data?: { jobId?: string } })?.data?.jobId
      if (!jobId) {
        throw new Error('Export job did not return a jobId')
      }
      await pollLiveJob(jobId)
    } catch (err: unknown) {
      setError(await parseAxiosBlobError(err))
      setStatusLabel(null)
      setProgress(null)
    } finally {
      setLoading(false)
    }
  }

  return (
    <div style={style}>
      <button type="button" onClick={handleExport} disabled={loading} style={{ width: style?.width ?? undefined }}>
        {loading ? 'Exporting…' : label}
      </button>
      {loading && (
        <div style={{ marginTop: '0.5rem', maxWidth: typeof style?.width === 'number' ? style.width : 320 }}>
          <div
            role="progressbar"
            aria-valuemin={0}
            aria-valuemax={100}
            aria-valuenow={progressPct ?? undefined}
            aria-label="Things to Do export progress"
            style={{
              position: 'relative',
              height: 8,
              borderRadius: 4,
              background: '#e9ecef',
              overflow: 'hidden',
            }}
          >
            {progressPct != null ? (
              <div
                style={{
                  height: '100%',
                  width: `${progressPct}%`,
                  background: '#98ca3f',
                  borderRadius: 4,
                  transition: 'width 0.35s ease',
                }}
              />
            ) : (
              <div
                style={{
                  position: 'absolute',
                  top: 0,
                  bottom: 0,
                  width: '40%',
                  background: '#98ca3f',
                  borderRadius: 4,
                  animation: 'ttd-export-indeterminate 1.2s ease-in-out infinite',
                }}
              />
            )}
          </div>
          {statusLabel && !error && (
            <p style={{ margin: '0.35rem 0 0', fontSize: '0.75rem', color: '#666' }}>
              {statusLabel}
              {progressPct != null ? ` (${progressPct}%)` : ''}
            </p>
          )}
        </div>
      )}
      {error && (
        <p style={{ margin: '0.35rem 0 0', fontSize: '0.75rem', color: '#dc3545' }}>{error}</p>
      )}
      <style>{`
        @keyframes ttd-export-indeterminate {
          0% { left: -40%; }
          100% { left: 100%; }
        }
      `}</style>
    </div>
  )
}

// ─── Admin Overview ─────────────────────────────────────────────────────────

function AdminOverview() {
  const { data: stats, isLoading } = useQuery({
    queryKey: ['admin-stats'],
    queryFn: () => api.admin.getSystemStats(),
  })
  const s = stats?.data || {}

  if (isLoading) return <div className="grid" style={{ gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))' }}><CardSkeleton /><CardSkeleton /></div>

  return (
    <div>
      <PageHeader title="System Overview" />
      <div className="grid" style={{ gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: '1rem' }}>
        <div className="card"><h4 style={{ margin: '0 0 0.5rem', color: '#666', fontSize: '0.85rem' }}>Total Vendors</h4><p style={{ fontSize: '2rem', fontWeight: 700, margin: 0 }}>{s.totalVendors || 0}</p></div>
        <div className="card"><h4 style={{ margin: '0 0 0.5rem', color: '#666', fontSize: '0.85rem' }}>Total Revenue</h4><p style={{ fontSize: '2rem', fontWeight: 700, margin: 0 }}>${s.totalRevenue || 0}</p></div>
      </div>
      <div style={{ marginTop: '2rem' }}>
        <h3>Quick Actions</h3>
        <div style={{ display: 'flex', gap: '0.75rem', flexWrap: 'wrap', marginTop: '1rem' }}>
          <PermissionGate permission="vendors:list"><Link to="/admin/vendors"><button>Manage Vendors</button></Link></PermissionGate>
          <PermissionGate permission="users:list"><Link to="/admin/users"><button>Manage Users</button></Link></PermissionGate>
          <PermissionGate permission="plans:list"><Link to="/admin/pricing-tiers"><button>Pricing Tiers</button></Link></PermissionGate>
          <PermissionGate permission="features:list"><Link to="/admin/features"><button>Features</button></Link></PermissionGate>
          <PermissionGate permission="fees:list"><Link to="/admin/fees"><button>Fee Plans</button></Link></PermissionGate>
          <PermissionGate permission="providers:list"><Link to="/admin/providers"><button>Providers</button></Link></PermissionGate>
          <PermissionGate permission="api_keys:list"><Link to="/admin/ai-connections"><button>AI Connections</button></Link></PermissionGate>
          <PermissionGate permission="api_keys:list"><Link to="/admin/api-keys"><button>API Keys</button></Link></PermissionGate>
          <ThingsToDoCsvExportButton allEligible />
        </div>
      </div>
    </div>
  )
}

// ─── Vendor Management ──────────────────────────────────────────────────────

// Plan labels resolved from API response (v.planName)

function Indicator({ ok, label }: { ok: boolean; label: string }) {
  return (
    <span style={{
      display: 'inline-flex', alignItems: 'center', gap: '0.35rem',
      fontSize: '0.75rem', color: ok ? 'var(--color-gray-700)' : 'var(--color-gray-400)',
    }}>
      <span style={{
        width: 8, height: 8, borderRadius: '50%', flexShrink: 0,
        background: ok ? '#98ca3f' : '#d1d5db',
      }} />
      {label}
    </span>
  )
}

type SortKey = 'vendor' | 'status' | 'plan' | 'onboarding' | 'storefront' | 'joined'
type SortDir = 'asc' | 'desc'

const SORT_KEY_TO_COLUMN: Record<SortKey, string> = {
  vendor: 'company_name',
  status: 'status',
  plan: 'plan_id',
  onboarding: 'onboarding_progress',
  storefront: 'username',
  joined: 'created_at',
}

function AdminVendors() {
  const qc = useQueryClient()
  const [search, setSearch] = useState('')
  const [debouncedSearch, setDebouncedSearch] = useState('')
  const [bokunId, setBokunId] = useState('')
  const [debouncedBokunId, setDebouncedBokunId] = useState('')
  const [statusFilter, setStatusFilter] = useState<string>('')
  const [page, setPage] = useState(1)
  const [sortKey, setSortKey] = useState<SortKey>('joined')
  const [sortDir, setSortDir] = useState<SortDir>('desc')

  // Debounce search input
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(search)
      setPage(1)
    }, 300)
    return () => clearTimeout(timer)
  }, [search])

  // Debounce Bokun ID input
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedBokunId(bokunId.trim())
      setPage(1)
    }, 300)
    return () => clearTimeout(timer)
  }, [bokunId])

  const queryParams = {
    page,
    limit: 10,
    status: statusFilter || undefined,
    search: debouncedSearch || undefined,
    bokunId: debouncedBokunId || undefined,
    sortBy: SORT_KEY_TO_COLUMN[sortKey],
    sortOrder: sortDir,
  }

  const { data, isLoading, isFetching } = useQuery({
    queryKey: ['admin-vendors', queryParams],
    queryFn: () => api.vendors.list(queryParams),
    placeholderData: (prev: any) => prev,
  })

  const vendors = (data?.data?.data?.items || data?.data?.items || []) as any[]
  const pagination = data?.data?.data?.pagination || data?.data?.pagination || { total: 0, page: 1, pages: 1 }
  // Present when a Bokun ID belongs to a supplier no vendor has claimed.
  const unclaimedMerchant = data?.data?.data?.unclaimedMerchant || data?.data?.unclaimedMerchant || null
  const total = pagination.total || 0

  const handleSort = (key: SortKey) => {
    if (sortKey === key) {
      setSortDir(d => d === 'asc' ? 'desc' : 'asc')
    } else {
      setSortKey(key)
      setSortDir('asc')
    }
    setPage(1)
  }

  const sortArrow = (key: SortKey) => sortKey === key ? (sortDir === 'asc' ? ' \u25B2' : ' \u25BC') : ''

  const activateMutation = useMutation({
    mutationFn: (id: string) => api.admin.vendors.activate(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['admin-vendors'] }),
    onError: (err: any) => alert(err?.response?.data?.error?.message || err?.message || 'Failed to activate vendor'),
  })
  const suspendMutation = useMutation({
    mutationFn: (id: string) => api.admin.vendors.suspend(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['admin-vendors'] }),
    onError: (err: any) => alert(err?.response?.data?.error?.message || err?.message || 'Failed to suspend vendor'),
  })

  const canActivate = useHasPermission('vendors:activate')
  const canSuspend = useHasPermission('vendors:suspend')

  if (isLoading) return <div><PageHeader title="Vendors" /><CardSkeleton /></div>

  return (
    <div>
      <PageHeader title="Vendors" />

      {/* Filters */}
      <div style={{ display: 'flex', gap: '0.75rem', marginBottom: '1rem', flexWrap: 'wrap', alignItems: 'center' }}>
        <input type="text" placeholder="Search by name or email..." value={search}
          onChange={(e) => setSearch(e.target.value)}
          style={{ flex: 1, minWidth: '200px', maxWidth: '400px', padding: '0.5rem 0.75rem', border: '1px solid var(--color-gray-300)', borderRadius: '6px', fontSize: '0.875rem' }} />
        <input type="text" inputMode="numeric" placeholder="Bokun ID" value={bokunId}
          onChange={(e) => setBokunId(e.target.value.replace(/[^0-9]/g, ''))}
          style={{ width: '130px', padding: '0.5rem 0.75rem', border: '1px solid var(--color-gray-300)', borderRadius: '6px', fontSize: '0.875rem' }} />
        <select value={statusFilter} onChange={(e) => { setStatusFilter(e.target.value); setPage(1) }}
          style={{ padding: '0.5rem 0.75rem', border: '1px solid var(--color-gray-300)', borderRadius: '6px', fontSize: '0.875rem', background: 'white' }}>
          <option value="">All statuses</option>
          <option value="active">Active</option>
          <option value="pending_validation">Pending</option>
          <option value="suspended">Suspended</option>
          <option value="inactive">Inactive</option>
        </select>
        {isFetching && !isLoading && (
          <span style={{ fontSize: '0.75rem', color: 'var(--color-gray-400)' }}>Loading...</span>
        )}
      </div>

      {/* A Bokun ID that resolves to a supplier nobody has claimed */}
      {unclaimedMerchant && (
        <div style={{ marginBottom: '1rem', padding: '0.75rem 1rem', borderRadius: '8px', border: '1px solid var(--color-gray-200)', background: 'var(--color-gray-50)', fontSize: '0.875rem', color: 'var(--color-gray-700)' }}>
          <strong>Bokun ID {unclaimedMerchant.external_vendor_id}</strong> belongs to{' '}
          <strong>{unclaimedMerchant.name}</strong>, which no vendor has claimed
          ({unclaimedMerchant.product_count} product{unclaimedMerchant.product_count === 1 ? '' : 's'} ingested).
        </div>
      )}

      {/* Table */}
      <div style={{ background: 'white', borderRadius: '8px', border: '1px solid var(--color-gray-200)', overflowX: 'auto', WebkitOverflowScrolling: 'touch' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: '900px' }}>
            <thead>
              <tr style={{ borderBottom: '2px solid var(--color-gray-200)', background: 'var(--color-gray-50)' }}>
                {([
                  ['vendor', 'Vendor', 'left'],
                  ['status', 'Status', 'left'],
                  ['plan', 'Plan', 'left'],
                  ['onboarding', 'Onboarding', 'left'],
                  ['storefront', 'Storefront', 'left'],
                  ['joined', 'Joined', 'left'],
                ] as [SortKey, string, string][]).map(([key, label, align]) => (
                  <th key={key}
                    onClick={() => handleSort(key)}
                    style={{ padding: '0.75rem 1rem', textAlign: align as any, fontSize: '0.75rem', fontWeight: 600, textTransform: 'uppercase', color: 'var(--color-gray-600)', cursor: 'pointer', userSelect: 'none', whiteSpace: 'nowrap' }}>
                    {label}{sortArrow(key)}
                  </th>
                ))}
                <th style={{ padding: '0.75rem 1rem', textAlign: 'left', fontSize: '0.75rem', fontWeight: 600, textTransform: 'uppercase', color: 'var(--color-gray-600)', whiteSpace: 'nowrap' }}>Bokun ID</th>
                <th style={{ padding: '0.75rem 1rem', textAlign: 'right', fontSize: '0.75rem', fontWeight: 600, textTransform: 'uppercase', color: 'var(--color-gray-600)' }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {vendors.map((v: any, idx: number) => {
                const id = v.vendorId || v.vendor_id || v.id
                const name = v.companyName || v.company_name || v.name
                const type = v.businessType || v.business_type
                const country = v.contactInfo?.country
                const planLabel = v.planName || 'Free'
                const subActive = v.subscriptionStatus === 'active' || v.subscriptionStatus === 'trialing'
                const isSuspended = v.status === 'suspended'
                const ob = v.onboarding || { steps: {}, completedCount: 0, totalSteps: 5, percentage: 0, feedCount: 0, productCount: 0 }
                const pct = ob.percentage || 0

                return (
                  <tr key={id} style={{
                    borderBottom: idx < vendors.length - 1 ? '1px solid var(--color-gray-100)' : 'none',
                    cursor: 'pointer',
                  }}
                    onClick={() => window.location.href = `/admin/vendors/${id}`}
                    onMouseEnter={e => e.currentTarget.style.background = 'var(--color-gray-50)'}
                    onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
                  >
                    {/* Vendor */}
                    <td style={{ padding: '0.75rem 1rem' }}>
                      <div style={{ fontWeight: 600, color: 'var(--color-gray-900)', marginBottom: '0.15rem' }}>{name}</div>
                      <div style={{ fontSize: '0.75rem', color: 'var(--color-gray-500)' }}>
                        {v.username ? `@${v.username}` : type ? <span style={{ textTransform: 'capitalize' }}>{type}</span> : ''}
                        {country && <span> -- {country}</span>}
                      </div>
                    </td>

                    {/* Status */}
                    <td style={{ padding: '0.75rem 1rem' }}>
                      <StatusBadge status={v.status} />
                      {isSuspended && (
                        <div style={{ fontSize: '0.65rem', color: '#dc3545', fontWeight: 600, marginTop: '0.2rem' }}>
                          Blocked
                        </div>
                      )}
                    </td>

                    {/* Plan */}
                    <td style={{ padding: '0.75rem 1rem' }}>
                      <div style={{ fontSize: '0.875rem', fontWeight: 500, color: 'var(--color-gray-900)' }}>{planLabel}</div>
                      {v.subscriptionStatus && v.subscriptionStatus !== 'none' && (
                        <div style={{
                          fontSize: '0.7rem', marginTop: '0.2rem', textTransform: 'capitalize',
                          color: subActive ? '#98ca3f' : '#dc3545',
                        }}>
                          {v.subscriptionStatus}
                        </div>
                      )}
                    </td>

                    {/* Onboarding */}
                    <td style={{ padding: '0.75rem 1rem' }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.35rem' }}>
                        <div style={{ flex: 1, height: '6px', background: 'var(--color-gray-200)', borderRadius: '3px', overflow: 'hidden', maxWidth: '80px' }}>
                          <div style={{ width: `${pct}%`, height: '100%', borderRadius: '3px', background: pct === 100 ? '#98ca3f' : 'var(--color-sky-blue)', transition: 'width 0.3s' }} />
                        </div>
                        <span style={{ fontSize: '0.7rem', fontWeight: 600, color: pct === 100 ? '#98ca3f' : 'var(--color-gray-500)' }}>{pct}%</span>
                      </div>
                      <div style={{ display: 'flex', flexDirection: 'column', gap: '0.15rem' }}>
                        <Indicator ok={ob.steps.provider} label="Provider" />
                        <Indicator ok={ob.steps.feeds} label={`Feeds${ob.feedCount > 0 ? ` (${ob.feedCount})` : ''}`} />
                        <Indicator ok={ob.steps.products} label={`Products${ob.productCount > 0 ? ` (${ob.productCount})` : ''}`} />
                        <Indicator ok={ob.steps.payment} label={ob.steps.payment ? `${(v.paymentProvider || 'stripe').charAt(0).toUpperCase() + (v.paymentProvider || 'stripe').slice(1)} (active)` : v.stripeAccountId ? 'Payment (incomplete)' : 'Payment'} />
                        <Indicator ok={ob.steps.agreement} label="Agreement" />
                        <Indicator ok={ob.steps.storefront} label="Storefront" />
                      </div>
                    </td>

                    {/* Storefront */}
                    <td style={{ padding: '0.75rem 1rem' }}>
                      {v.storefrontUrl ? (
                        <a href={v.storefrontUrl} target="_blank" rel="noopener noreferrer"
                          onClick={(e) => e.stopPropagation()}
                          style={{ fontSize: '0.75rem', color: 'var(--color-sky-blue)', textDecoration: 'none', wordBreak: 'break-all' }}>
                          {v.customDomain && v.customDomainStatus === 'active' ? v.customDomain : v.username ? `@${v.username}` : 'View'}
                        </a>
                      ) : (
                        <span style={{ fontSize: '0.75rem', color: 'var(--color-gray-400)' }}>Not set up</span>
                      )}
                    </td>

                    {/* Joined */}
                    <td style={{ padding: '0.75rem 1rem', fontSize: '0.8rem', color: 'var(--color-gray-500)' }}>
                      {v.createdAt ? new Date(v.createdAt).toLocaleDateString() : '--'}
                    </td>

                    {/* Bokun ID */}
                    <td style={{ padding: '0.75rem 1rem', fontSize: '0.8rem', color: 'var(--color-gray-700)', whiteSpace: 'nowrap' }}>
                      {(v.bokunIds || []).length > 0
                        ? (v.bokunIds as number[]).join(', ')
                        : <span style={{ color: 'var(--color-gray-400)' }}>--</span>}
                    </td>

                    {/* Actions */}
                    <td style={{ padding: '0.75rem 1rem', textAlign: 'right' }} onClick={(e) => e.stopPropagation()}>
                      <div style={{ display: 'flex', gap: '0.5rem', justifyContent: 'flex-end' }}>
                        <Link to={`/admin/vendors/${id}`}>
                          <button style={{
                            fontSize: '0.75rem', padding: '0.35rem 0.75rem', borderRadius: '4px',
                            border: '1px solid var(--color-gray-300)', background: 'white', color: 'var(--color-gray-700)', cursor: 'pointer',
                          }}>View</button>
                        </Link>
                        {canActivate && v.status !== 'active' && (
                          <button onClick={() => activateMutation.mutate(String(id))}
                            disabled={activateMutation.isPending}
                            style={{
                              fontSize: '0.75rem', padding: '0.35rem 0.75rem', borderRadius: '4px',
                              border: '1px solid #98ca3f', background: 'rgba(152,202,63,0.1)', color: '#1a2e44', cursor: 'pointer',
                            }}>
                            Activate
                          </button>
                        )}
                        {canSuspend && v.status !== 'suspended' && (
                          <button onClick={() => suspendMutation.mutate(String(id))}
                            disabled={suspendMutation.isPending}
                            style={{
                              fontSize: '0.75rem', padding: '0.35rem 0.75rem', borderRadius: '4px',
                              border: 'none', background: '#dc3545', color: '#fff', cursor: 'pointer',
                            }}>
                            Suspend
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                )
              })}
            </tbody>
          </table>
      </div>
      {vendors.length === 0 && <p style={{ textAlign: 'center', color: 'var(--color-gray-500)', marginTop: '2rem' }}>No vendors found.</p>}
      <Pagination page={page} totalItems={total} onPageChange={setPage} />
    </div>
  )
}

// ─── User Management ────────────────────────────────────────────────────────

function AdminUsers() {
  const qc = useQueryClient()
  const [showInvite, setShowInvite] = useState(false)
  const [inviteData, setInviteData] = useState({ email: '', firstName: '', lastName: '', role: 'VIEWER' })
  const [inviteError, setInviteError] = useState('')
  const [userPage, setUserPage] = useState(1)

  const { data, isLoading } = useQuery({ queryKey: ['admin-users'], queryFn: () => api.admin.users.list() })
  const users = data?.data?.data?.users || data?.data?.users || []

  const inviteMutation = useMutation({
    mutationFn: (d: any) => api.admin.users.invite(d),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['admin-users'] }); setShowInvite(false); setInviteData({ email: '', firstName: '', lastName: '', role: 'VIEWER' }) },
    onError: (err: any) => setInviteError(err.response?.data?.error?.message || 'Failed to invite user'),
  })

  const removeMutation = useMutation({
    mutationFn: (id: string) => api.admin.users.remove(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['admin-users'] }),
  })

  const resendMutation = useMutation({
    mutationFn: (id: string) => api.admin.users.resendInvite(id),
    onSuccess: () => alert('Invite email resent'),
    onError: (err: any) => alert(err.response?.data?.error?.message || 'Failed to resend invite'),
  })

  const roleChangeMutation = useMutation({
    mutationFn: ({ userId, role }: { userId: string; role: string }) => api.admin.users.updateRole(userId, role),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['admin-users'] }),
  })

  const canInvite = useHasPermission('users:invite')
  const canRemove = useHasPermission('users:remove')

  const superAdminRoles = ['SUPER_ADMIN', 'ADMIN', 'MODERATOR', 'VIEWER']

  if (isLoading) return <div><PageHeader title="Team Members" /><CardSkeleton /></div>

  return (
    <div>
      <PageHeader title="Team Members" action={
        canInvite ? <button onClick={() => setShowInvite(!showInvite)}>Invite User</button> : undefined
      } />

      {showInvite && (
        <div className="card" style={{ marginBottom: '1.5rem' }}>
          <h3 style={{ marginTop: 0 }}>Invite Team Member</h3>
          {inviteError && <div style={{ color: '#dc3545', marginBottom: '1rem' }}>{inviteError}</div>}
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(min(200px, 100%), 1fr))', gap: '1rem' }}>
            <input placeholder="First Name" value={inviteData.firstName} onChange={(e) => setInviteData({ ...inviteData, firstName: e.target.value })} style={{ padding: '0.5rem', border: '1px solid #ccc', borderRadius: '4px' }} />
            <input placeholder="Last Name" value={inviteData.lastName} onChange={(e) => setInviteData({ ...inviteData, lastName: e.target.value })} style={{ padding: '0.5rem', border: '1px solid #ccc', borderRadius: '4px' }} />
          </div>
          <input placeholder="Email" type="email" value={inviteData.email} onChange={(e) => setInviteData({ ...inviteData, email: e.target.value })} style={{ width: '100%', marginTop: '0.75rem', padding: '0.5rem', border: '1px solid #ccc', borderRadius: '4px' }} />
          <select value={inviteData.role} onChange={(e) => setInviteData({ ...inviteData, role: e.target.value })} style={{ width: '100%', marginTop: '0.75rem', padding: '0.5rem' }}>
            {superAdminRoles.map((r) => <option key={r} value={r}>{ROLE_LABELS[r]} -- {ROLE_DESCRIPTIONS[r]}</option>)}
          </select>
          <div style={{ display: 'flex', gap: '0.75rem', marginTop: '1rem' }}>
            <button onClick={() => inviteMutation.mutate(inviteData)} disabled={inviteMutation.isPending}>
              {inviteMutation.isPending ? 'Inviting...' : 'Send Invite'}
            </button>
            <button onClick={() => setShowInvite(false)} style={{ background: '#e5e7eb', color: '#333' }}>Cancel</button>
          </div>
        </div>
      )}

      <table style={{ width: '100%', borderCollapse: 'collapse' }}>
        <thead>
          <tr style={{ borderBottom: '2px solid #e5e7eb', textAlign: 'left' }}>
            <th style={{ padding: '0.75rem' }}>Name</th>
            <th style={{ padding: '0.75rem' }}>Email</th>
            <th style={{ padding: '0.75rem' }}>Role</th>
            <th style={{ padding: '0.75rem' }}>Status</th>
            <th style={{ padding: '0.75rem' }}>Actions</th>
          </tr>
        </thead>
        <tbody>
          {paginate(users, userPage).map((u: any) => (
            <tr key={u.userId || u.user_id} style={{ borderBottom: '1px solid #f3f4f6' }}>
              <td style={{ padding: '0.75rem' }}>{u.firstName || u.first_name} {u.lastName || u.last_name}</td>
              <td style={{ padding: '0.75rem' }}>{u.email}</td>
              <td style={{ padding: '0.75rem' }}>
                <PermissionGate permission="users:update_role" fallback={<StatusBadge status={u.role} />}>
                  <select
                    value={u.role}
                    onChange={(e) => {
                      if (confirm(`Change role to ${ROLE_LABELS[e.target.value]}?`)) {
                        roleChangeMutation.mutate({ userId: String(u.userId || u.user_id), role: e.target.value })
                      }
                    }}
                    style={{ padding: '0.25rem', fontSize: '0.8rem', borderRadius: '4px', border: '1px solid #ccc' }}
                  >
                    {superAdminRoles.map((r) => <option key={r} value={r}>{ROLE_LABELS[r]}</option>)}
                  </select>
                </PermissionGate>
              </td>
              <td style={{ padding: '0.75rem' }}><StatusBadge status={u.status} /></td>
              <td style={{ padding: '0.75rem' }}>
                <div style={{ display: 'flex', gap: '0.5rem' }}>
                  {u.status === 'invited' && canInvite && (
                    <button onClick={() => resendMutation.mutate(String(u.userId || u.user_id))}
                      disabled={resendMutation.isPending}
                      style={{ fontSize: '0.8rem', padding: '0.25rem 0.75rem', background: 'var(--color-sky-blue)', color: '#fff', border: 'none', borderRadius: '4px', cursor: 'pointer' }}>
                      {resendMutation.isPending ? 'Sending...' : 'Resend'}
                    </button>
                  )}
                  {canRemove && (
                    <button onClick={() => { if (confirm('Remove this user?')) removeMutation.mutate(String(u.userId || u.user_id)) }}
                      style={{ fontSize: '0.8rem', padding: '0.25rem 0.75rem', background: '#dc3545', color: '#fff', border: 'none', borderRadius: '4px', cursor: 'pointer' }}>
                      Remove
                    </button>
                  )}
                </div>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      {users.length === 0 && <p style={{ textAlign: 'center', color: '#666', marginTop: '2rem' }}>No team members yet.</p>}
      <Pagination page={userPage} totalItems={users.length} onPageChange={setUserPage} />
    </div>
  )
}

// Feature gates still use the numeric tier. Commercial tiers are edited on Pricing Tiers.
const TIER_LABELS: Record<number, string> = { 0: 'Free', 1: 'Growth', 2: 'Pro', 3: 'Scale', 4: 'Volume', 5: 'Infrastructure' }

// ─── Features ───────────────────────────────────────────────────────────────

function AdminFeatures() {
  const qc = useQueryClient()
  const [showForm, setShowForm] = useState(false)
  const [editingFeature, setEditingFeature] = useState<any>(null)
  const [form, setForm] = useState({ name: '', slug: '', description: '', tier: '0' })
  const [formError, setFormError] = useState('')
  const [featPage, setFeatPage] = useState(1)

  const { data, isLoading } = useQuery({ queryKey: ['admin-features'], queryFn: () => api.admin.features.list() })
  const features = data?.data?.data?.features || []

  const resetForm = () => {
    setShowForm(false)
    setEditingFeature(null)
    setForm({ name: '', slug: '', description: '', tier: '0' })
    setFormError('')
  }

  const startEdit = (feature: any) => {
    setEditingFeature(feature)
    setForm({ name: feature.name, slug: feature.slug, description: feature.description || '', tier: String(feature.tier) })
    setShowForm(true)
  }

  const createMutation = useMutation({
    mutationFn: (d: any) => api.admin.features.create(d),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['admin-features'] }); resetForm() },
    onError: (err: any) => setFormError(err.response?.data?.error?.message || 'Failed to create'),
  })

  const updateMutation = useMutation({
    mutationFn: ({ id, data: d }: { id: string; data: any }) => api.admin.features.update(id, d),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['admin-features'] }); resetForm() },
    onError: (err: any) => setFormError(err.response?.data?.error?.message || 'Failed to update'),
  })

  const deactivateMutation = useMutation({
    mutationFn: (id: string) => api.admin.features.deactivate(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['admin-features'] }),
  })

  const canCreate = useHasPermission('features:create')
  const canUpdate = useHasPermission('features:update')

  const handleSubmit = () => {
    const payload = { ...form, tier: parseInt(form.tier) }
    if (editingFeature) {
      const { slug: _slug, ...updatePayload } = payload
      updateMutation.mutate({ id: String(editingFeature.feature_id), data: updatePayload })
    } else {
      createMutation.mutate(payload)
    }
  }

  if (isLoading) return <div><PageHeader title="Features" /><CardSkeleton /></div>

  return (
    <div>
      <PageHeader title="Features" action={canCreate ? <button onClick={() => { resetForm(); setShowForm(true) }}>Create Feature</button> : undefined} />

      {showForm && (
        <div className="card" style={{ marginBottom: '1.5rem' }}>
          <h3 style={{ marginTop: 0 }}>{editingFeature ? 'Edit Feature' : 'New Feature'}</h3>
          {formError && <div style={{ color: '#dc3545', marginBottom: '1rem' }}>{formError}</div>}
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(min(200px, 100%), 1fr))', gap: '1rem' }}>
            <div>
              <label style={{ display: 'block', marginBottom: '0.25rem', fontSize: '0.85rem' }}>Name *</label>
              <input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} style={{ width: '100%', padding: '0.5rem', border: '1px solid #ccc', borderRadius: '4px' }} />
            </div>
            <div>
              <label style={{ display: 'block', marginBottom: '0.25rem', fontSize: '0.85rem' }}>Slug *{editingFeature && ' (read-only)'}</label>
              <input value={form.slug} onChange={(e) => setForm({ ...form, slug: e.target.value })} disabled={!!editingFeature}
                placeholder="e.g. analytics_dashboard" style={{ width: '100%', padding: '0.5rem', border: '1px solid #ccc', borderRadius: '4px', background: editingFeature ? '#f3f4f6' : undefined }} />
            </div>
            <div>
              <label style={{ display: 'block', marginBottom: '0.25rem', fontSize: '0.85rem' }}>Required Tier *</label>
              <select value={form.tier} onChange={(e) => setForm({ ...form, tier: e.target.value })} style={{ width: '100%', padding: '0.5rem' }}>
                <option value="0">0 - Free</option>
                <option value="1">1 - Growth</option>
                <option value="2">2 - Pro</option>
                <option value="3">3 - Scale</option>
                <option value="4">4 - Volume</option>
                <option value="5">5 - Infrastructure</option>
              </select>
            </div>
            <div>
              <label style={{ display: 'block', marginBottom: '0.25rem', fontSize: '0.85rem' }}>Description</label>
              <input value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} style={{ width: '100%', padding: '0.5rem', border: '1px solid #ccc', borderRadius: '4px' }} />
            </div>
          </div>
          <div style={{ display: 'flex', gap: '0.75rem', marginTop: '1rem' }}>
            <button onClick={handleSubmit} disabled={createMutation.isPending || updateMutation.isPending}>
              {(createMutation.isPending || updateMutation.isPending) ? 'Saving...' : editingFeature ? 'Update' : 'Create'}
            </button>
            <button onClick={resetForm} style={{ background: '#e5e7eb', color: '#333' }}>Cancel</button>
          </div>
        </div>
      )}

      <table style={{ width: '100%', borderCollapse: 'collapse' }}>
        <thead>
          <tr style={{ borderBottom: '2px solid #e5e7eb', textAlign: 'left' }}>
            <th style={{ padding: '0.75rem' }}>Name</th>
            <th style={{ padding: '0.75rem' }}>Slug</th>
            <th style={{ padding: '0.75rem' }}>Required Tier</th>
            <th style={{ padding: '0.75rem' }}>Status</th>
            <th style={{ padding: '0.75rem' }}>Actions</th>
          </tr>
        </thead>
        <tbody>
          {paginate(features, featPage).map((f: any) => (
            <tr key={f.feature_id} style={{ borderBottom: '1px solid #f3f4f6' }}>
              <td style={{ padding: '0.75rem', fontWeight: 500 }}>{f.name}</td>
              <td style={{ padding: '0.75rem', fontFamily: 'monospace', fontSize: '0.85rem' }}>{f.slug}</td>
              <td style={{ padding: '0.75rem' }}>{f.tier} ({TIER_LABELS[f.tier] || `Tier ${f.tier}`})</td>
              <td style={{ padding: '0.75rem' }}><StatusBadge status={f.status === 'active' ? 'active' : 'inactive'} /></td>
              <td style={{ padding: '0.75rem' }}>
                <div style={{ display: 'flex', gap: '0.5rem' }}>
                  {canUpdate && f.status === 'active' && (
                    <button onClick={() => startEdit(f)} style={{ fontSize: '0.8rem', padding: '0.25rem 0.75rem' }}>Edit</button>
                  )}
                  {f.status === 'active' && canUpdate && (
                    <button onClick={() => { if (confirm('Deactivate this feature?')) deactivateMutation.mutate(String(f.feature_id)) }}
                      style={{ fontSize: '0.8rem', padding: '0.25rem 0.75rem', background: '#fee2e2', color: '#991b1b' }}>
                      Deactivate
                    </button>
                  )}
                </div>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      {features.length === 0 && <p style={{ textAlign: 'center', color: '#666', marginTop: '2rem' }}>No features created yet.</p>}
      <Pagination page={featPage} totalItems={features.length} onPageChange={setFeatPage} />
    </div>
  )
}

// ─── Fee Plans ──────────────────────────────────────────────────────────────

function AdminFees() {
  const qc = useQueryClient()
  const [showForm, setShowForm] = useState(false)
  const [editingPlan, setEditingPlan] = useState<any>(null)
  const [form, setForm] = useState({ name: '', percentage_fee: '', fixed_fee: '', currency: 'USD', card_fee_bearer: 'vendor' as string, is_default: false })
  const [formError, setFormError] = useState('')
  const [feePage, setFeePage] = useState(1)

  const { data, isLoading } = useQuery({ queryKey: ['admin-fee-plans'], queryFn: () => api.admin.feePlans.list() })
  const plans = data?.data?.data?.plans || []

  const resetForm = () => {
    setShowForm(false)
    setEditingPlan(null)
    setForm({ name: '', percentage_fee: '', fixed_fee: '', currency: 'USD', card_fee_bearer: 'vendor', is_default: false })
    setFormError('')
  }

  const startEdit = (plan: any) => {
    setEditingPlan(plan)
    setForm({
      name: plan.name,
      percentage_fee: String(plan.percentage_fee),
      fixed_fee: plan.fixed_fee > 0 ? String(plan.fixed_fee) : '',
      currency: plan.currency || 'USD',
      card_fee_bearer: plan.card_fee_bearer || 'vendor',
      is_default: plan.is_default,
    })
    setShowForm(true)
  }

  const createMutation = useMutation({
    mutationFn: (d: any) => api.admin.feePlans.create(d),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['admin-fee-plans'] }); resetForm() },
    onError: (err: any) => setFormError(err.response?.data?.error?.message || 'Failed to create'),
  })

  const updateMutation = useMutation({
    mutationFn: ({ id, data: d }: { id: string; data: any }) => api.admin.feePlans.update(id, d),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['admin-fee-plans'] }); resetForm() },
    onError: (err: any) => setFormError(err.response?.data?.error?.message || 'Failed to update'),
  })

  const setDefaultMutation = useMutation({
    mutationFn: (id: string) => api.admin.feePlans.setDefault(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['admin-fee-plans'] }),
  })

  const deactivateMutation = useMutation({
    mutationFn: (id: string) => api.admin.feePlans.deactivate(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['admin-fee-plans'] }),
  })

  const canCreate = useHasPermission('fees:create')
  const canUpdate = useHasPermission('fees:update')

  const handleSubmit = () => {
    const payload = { ...form, percentage_fee: parseFloat(form.percentage_fee), fixed_fee: form.fixed_fee ? parseFloat(form.fixed_fee) : undefined }
    if (editingPlan) {
      updateMutation.mutate({ id: String(editingPlan.fee_plan_id), data: payload })
    } else {
      createMutation.mutate(payload)
    }
  }

  if (isLoading) return <div><PageHeader title="Fee Plans" /><CardSkeleton /></div>

  return (
    <div>
      <PageHeader title="Fee Plans" action={canCreate ? <button onClick={() => { resetForm(); setShowForm(true) }}>Create Fee Plan</button> : undefined} />

      {showForm && (
        <div className="card" style={{ marginBottom: '1.5rem' }}>
          <h3 style={{ marginTop: 0 }}>{editingPlan ? 'Edit Fee Plan' : 'New Fee Plan'}</h3>
          {formError && <div style={{ color: '#dc3545', marginBottom: '1rem' }}>{formError}</div>}
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(min(200px, 100%), 1fr))', gap: '1rem' }}>
            <div>
              <label style={{ display: 'block', marginBottom: '0.25rem', fontSize: '0.85rem' }}>Name *</label>
              <input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} style={{ width: '100%', padding: '0.5rem', border: '1px solid #ccc', borderRadius: '4px' }} />
            </div>
            <div>
              <label style={{ display: 'block', marginBottom: '0.25rem', fontSize: '0.85rem' }}>Percentage Fee (%) *</label>
              <input type="number" step="0.01" value={form.percentage_fee} onChange={(e) => setForm({ ...form, percentage_fee: e.target.value })} style={{ width: '100%', padding: '0.5rem', border: '1px solid #ccc', borderRadius: '4px' }} />
            </div>
            <div>
              <label style={{ display: 'block', marginBottom: '0.25rem', fontSize: '0.85rem' }}>Fixed Fee</label>
              <input type="number" step="0.01" value={form.fixed_fee} onChange={(e) => setForm({ ...form, fixed_fee: e.target.value })} style={{ width: '100%', padding: '0.5rem', border: '1px solid #ccc', borderRadius: '4px' }} />
            </div>
            <div>
              <label style={{ display: 'block', marginBottom: '0.25rem', fontSize: '0.85rem' }}>Currency</label>
              <input value={form.currency} onChange={(e) => setForm({ ...form, currency: e.target.value })} style={{ width: '100%', padding: '0.5rem', border: '1px solid #ccc', borderRadius: '4px' }} />
            </div>
            <div>
              <label style={{ display: 'block', marginBottom: '0.25rem', fontSize: '0.85rem' }}>Card Processing Fee Bearer</label>
              <select value={form.card_fee_bearer} onChange={(e) => setForm({ ...form, card_fee_bearer: e.target.value })} style={{ width: '100%', padding: '0.5rem' }}>
                <option value="vendor">Vendor (absorbed from vendor balance)</option>
                <option value="customer">Customer (added as surcharge)</option>
              </select>
              <p style={{ margin: '0.25rem 0 0', fontSize: '0.7rem', color: '#888' }}>
                {form.card_fee_bearer === 'vendor'
                  ? 'Stripe card processing fee is deducted from the vendor\'s balance. Customer sees no fee.'
                  : 'Stripe card processing fee is added to the customer\'s total at checkout.'}
              </p>
            </div>
          </div>
          <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginTop: '0.75rem' }}>
            <input type="checkbox" checked={form.is_default} onChange={(e) => setForm({ ...form, is_default: e.target.checked })} />
            Set as default fee plan
          </label>
          <div style={{ display: 'flex', gap: '0.75rem', marginTop: '1rem' }}>
            <button onClick={handleSubmit} disabled={createMutation.isPending || updateMutation.isPending}>
              {(createMutation.isPending || updateMutation.isPending) ? 'Saving...' : editingPlan ? 'Update' : 'Create'}
            </button>
            <button onClick={resetForm} style={{ background: '#e5e7eb', color: '#333' }}>Cancel</button>
          </div>
        </div>
      )}

      <table style={{ width: '100%', borderCollapse: 'collapse' }}>
        <thead>
          <tr style={{ borderBottom: '2px solid #e5e7eb', textAlign: 'left' }}>
            <th style={{ padding: '0.75rem' }}>Name</th>
            <th style={{ padding: '0.75rem' }}>Percentage</th>
            <th style={{ padding: '0.75rem' }}>Fixed Fee</th>
            <th style={{ padding: '0.75rem' }}>Card Fee Bearer</th>
            <th style={{ padding: '0.75rem' }}>Status</th>
            <th style={{ padding: '0.75rem' }}>Default</th>
            <th style={{ padding: '0.75rem' }}>Actions</th>
          </tr>
        </thead>
        <tbody>
          {paginate(plans, feePage).map((p: any) => (
            <tr key={p.fee_plan_id} style={{ borderBottom: '1px solid #f3f4f6' }}>
              <td style={{ padding: '0.75rem', fontWeight: 500 }}>{p.name}</td>
              <td style={{ padding: '0.75rem' }}>{p.percentage_fee}%</td>
              <td style={{ padding: '0.75rem' }}>{p.fixed_fee > 0 ? `${p.currency} ${p.fixed_fee}` : '--'}</td>
              <td style={{ padding: '0.75rem' }}>
                <span style={{
                  padding: '0.2rem 0.5rem',
                  borderRadius: '4px',
                  fontSize: '0.75rem',
                  fontWeight: 500,
                  background: p.card_fee_bearer === 'customer' ? '#fef3c7' : '#f0fdf4',
                  color: p.card_fee_bearer === 'customer' ? '#92400e' : '#166534',
                }}>
                  {p.card_fee_bearer === 'customer' ? 'Customer' : 'Vendor'}
                </span>
              </td>
              <td style={{ padding: '0.75rem' }}><StatusBadge status={p.status === 'active' ? 'active' : 'inactive'} /></td>
              <td style={{ padding: '0.75rem' }}>{p.is_default ? 'Yes' : '--'}</td>
              <td style={{ padding: '0.75rem' }}>
                <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
                  {canUpdate && p.status === 'active' && (
                    <button onClick={() => startEdit(p)}
                      style={{ fontSize: '0.8rem', padding: '0.25rem 0.75rem' }}>
                      Edit
                    </button>
                  )}
                  {!p.is_default && p.status === 'active' && (
                    <button onClick={() => setDefaultMutation.mutate(String(p.fee_plan_id))}
                      style={{ fontSize: '0.8rem', padding: '0.25rem 0.75rem' }}>
                      Set Default
                    </button>
                  )}
                  {!p.is_default && p.status === 'active' && canUpdate && (
                    <button onClick={() => { if (confirm('Deactivate this fee plan? Vendors using it will fall back to the default fee plan.')) deactivateMutation.mutate(String(p.fee_plan_id)) }}
                      style={{ fontSize: '0.8rem', padding: '0.25rem 0.75rem', background: '#fee2e2', color: '#991b1b' }}>
                      Deactivate
                    </button>
                  )}
                </div>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      {plans.length === 0 && <p style={{ textAlign: 'center', color: '#666', marginTop: '2rem' }}>No fee plans created yet.</p>}
      <Pagination page={feePage} totalItems={plans.length} onPageChange={setFeePage} />
    </div>
  )
}

// ─── Providers ─────────────────────────────────────────────────────────────

function AdminProviders() {
  const qc = useQueryClient()
  const [showForm, setShowForm] = useState(false)
  const [editingProvider, setEditingProvider] = useState<any>(null)
  const [form, setForm] = useState({ name: '', slug: '', fee_rate: '', description: '' })
  const [formError, setFormError] = useState('')
  const [provPage, setProvPage] = useState(1)

  const { data, isLoading } = useQuery({ queryKey: ['admin-providers'], queryFn: () => api.admin.providers.list() })
  const providers = data?.data?.data?.providers || []

  const resetForm = () => {
    setShowForm(false)
    setEditingProvider(null)
    setForm({ name: '', slug: '', fee_rate: '', description: '' })
    setFormError('')
  }

  const startEdit = (p: any) => {
    setEditingProvider(p)
    setForm({
      name: p.name,
      slug: p.slug,
      fee_rate: String(p.fee_rate * 100),
      description: p.description || '',
    })
    setShowForm(true)
  }

  const createMutation = useMutation({
    mutationFn: (d: any) => api.admin.providers.create(d),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['admin-providers'] }); resetForm() },
    onError: (err: any) => setFormError(err.response?.data?.error?.message || 'Failed to create'),
  })

  const updateMutation = useMutation({
    mutationFn: ({ id, data: d }: { id: string; data: any }) => api.admin.providers.update(id, d),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['admin-providers'] }); resetForm() },
    onError: (err: any) => setFormError(err.response?.data?.error?.message || 'Failed to update'),
  })

  const setDefaultMutation = useMutation({
    mutationFn: (id: string) => api.admin.providers.setDefault(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['admin-providers'] }),
  })

  const deactivateMutation = useMutation({
    mutationFn: (id: string) => api.admin.providers.deactivate(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['admin-providers'] }),
  })

  const canCreate = useHasPermission('providers:create')
  const canUpdate = useHasPermission('providers:update')

  const handleSubmit = () => {
    const feeRatePercent = parseFloat(form.fee_rate)
    if (isNaN(feeRatePercent) || feeRatePercent < 0 || feeRatePercent > 100) {
      setFormError('Fee rate must be between 0 and 100')
      return
    }
    const payload = {
      name: form.name,
      slug: form.slug,
      fee_rate: feeRatePercent / 100,
      description: form.description || undefined,
    }
    if (editingProvider) {
      updateMutation.mutate({ id: String(editingProvider.provider_id), data: payload })
    } else {
      createMutation.mutate(payload)
    }
  }

  const autoSlug = (name: string) => {
    const slug = name.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '')
    setForm((f) => ({ ...f, name, slug: editingProvider ? f.slug : slug }))
  }

  if (isLoading) return <div><PageHeader title="Providers" /><CardSkeleton /></div>

  return (
    <div>
      <PageHeader title="Providers" action={canCreate ? <button onClick={() => { resetForm(); setShowForm(true) }}>Add Provider</button> : undefined} />
      <p style={{ color: '#666', fontSize: '0.85rem', marginTop: '-1rem', marginBottom: '1.5rem' }}>
        Manage external booking providers and their fee rates. The slug must match the platform value in inventory feeds.
      </p>

      {showForm && (
        <div className="card" style={{ marginBottom: '1.5rem' }}>
          <h3 style={{ marginTop: 0 }}>{editingProvider ? 'Edit Provider' : 'New Provider'}</h3>
          {formError && <div style={{ color: '#dc3545', marginBottom: '1rem' }}>{formError}</div>}
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(min(200px, 100%), 1fr))', gap: '1rem' }}>
            <div>
              <label style={{ display: 'block', marginBottom: '0.25rem', fontSize: '0.85rem' }}>Name *</label>
              <input value={form.name} onChange={(e) => autoSlug(e.target.value)} style={{ width: '100%', padding: '0.5rem', border: '1px solid #ccc', borderRadius: '4px' }} />
            </div>
            <div>
              <label style={{ display: 'block', marginBottom: '0.25rem', fontSize: '0.85rem' }}>Slug *</label>
              <input value={form.slug} onChange={(e) => setForm({ ...form, slug: e.target.value.toLowerCase() })} style={{ width: '100%', padding: '0.5rem', border: '1px solid #ccc', borderRadius: '4px', fontFamily: 'monospace' }} />
              <p style={{ margin: '0.25rem 0 0', fontSize: '0.7rem', color: '#888' }}>
                Must match the platform value in inventory feeds (e.g. "bokun", "viator")
              </p>
            </div>
            <div>
              <label style={{ display: 'block', marginBottom: '0.25rem', fontSize: '0.85rem' }}>Fee Rate (%) *</label>
              <input type="number" step="0.01" min="0" max="100" value={form.fee_rate} onChange={(e) => setForm({ ...form, fee_rate: e.target.value })} style={{ width: '100%', padding: '0.5rem', border: '1px solid #ccc', borderRadius: '4px' }} />
              <p style={{ margin: '0.25rem 0 0', fontSize: '0.7rem', color: '#888' }}>
                Markup applied to customer-facing prices. E.g. 1.5 = 1.5%
              </p>
            </div>
            <div>
              <label style={{ display: 'block', marginBottom: '0.25rem', fontSize: '0.85rem' }}>Description</label>
              <input value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} style={{ width: '100%', padding: '0.5rem', border: '1px solid #ccc', borderRadius: '4px' }} />
            </div>
          </div>
          <div style={{ display: 'flex', gap: '0.75rem', marginTop: '1rem' }}>
            <button onClick={handleSubmit} disabled={createMutation.isPending || updateMutation.isPending}>
              {(createMutation.isPending || updateMutation.isPending) ? 'Saving...' : editingProvider ? 'Update' : 'Create'}
            </button>
            <button onClick={resetForm} style={{ background: '#e5e7eb', color: '#333' }}>Cancel</button>
          </div>
        </div>
      )}

      <table style={{ width: '100%', borderCollapse: 'collapse' }}>
        <thead>
          <tr style={{ borderBottom: '2px solid #e5e7eb', textAlign: 'left' }}>
            <th style={{ padding: '0.75rem' }}>Name</th>
            <th style={{ padding: '0.75rem' }}>Slug</th>
            <th style={{ padding: '0.75rem' }}>Fee Rate</th>
            <th style={{ padding: '0.75rem' }}>Status</th>
            <th style={{ padding: '0.75rem' }}>Default</th>
            <th style={{ padding: '0.75rem' }}>Actions</th>
          </tr>
        </thead>
        <tbody>
          {paginate(providers, provPage).map((p: any) => (
            <tr key={p.provider_id} style={{ borderBottom: '1px solid #f3f4f6' }}>
              <td style={{ padding: '0.75rem', fontWeight: 500 }}>{p.name}</td>
              <td style={{ padding: '0.75rem', fontFamily: 'monospace', fontSize: '0.85rem' }}>{p.slug}</td>
              <td style={{ padding: '0.75rem' }}>{(p.fee_rate * 100).toFixed(2)}%</td>
              <td style={{ padding: '0.75rem' }}><StatusBadge status={p.status === 'active' ? 'active' : 'inactive'} /></td>
              <td style={{ padding: '0.75rem' }}>{p.is_default ? 'Yes' : '--'}</td>
              <td style={{ padding: '0.75rem' }}>
                <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
                  {canUpdate && p.status === 'active' && (
                    <button onClick={() => startEdit(p)}
                      style={{ fontSize: '0.8rem', padding: '0.25rem 0.75rem' }}>
                      Edit
                    </button>
                  )}
                  {!p.is_default && p.status === 'active' && (
                    <button onClick={() => setDefaultMutation.mutate(String(p.provider_id))}
                      disabled={setDefaultMutation.isPending}
                      style={{ fontSize: '0.8rem', padding: '0.25rem 0.75rem' }}>
                      {setDefaultMutation.isPending ? 'Setting...' : 'Set Default'}
                    </button>
                  )}
                  {!p.is_default && p.status === 'active' && canUpdate && (
                    <button onClick={() => { if (confirm(`Deactivate ${p.name}? Products from this provider will use the default fee rate.`)) deactivateMutation.mutate(String(p.provider_id)) }}
                      style={{ fontSize: '0.8rem', padding: '0.25rem 0.75rem', background: '#fee2e2', color: '#991b1b' }}>
                      Deactivate
                    </button>
                  )}
                </div>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      {providers.length === 0 && <p style={{ textAlign: 'center', color: '#666', marginTop: '2rem' }}>No providers configured yet.</p>}
      <Pagination page={provPage} totalItems={providers.length} onPageChange={setProvPage} />
    </div>
  )
}

// ─── AI Connections (Admin — Global) ────────────────────────────────────────

function AdminAiConnections() {
  const qc = useQueryClient()
  const [showCreate, setShowCreate] = useState(false)
  const [credentials, setCredentials] = useState<{ client_id: string; client_secret: string; api_key: string; auth_username: string; auth_password: string } | null>(null)
  const [form, setForm] = useState({ name: '', description: '' })
  const [formError, setFormError] = useState('')
  const [copied, setCopied] = useState<string | null>(null)
  const [connPage, setConnPage] = useState(1)
  const [platformAppUrl, setPlatformAppUrl] = useState('')
  const [platformSaveError, setPlatformSaveError] = useState('')

  const { data, isLoading } = useQuery({ queryKey: ['admin-ai-connections'], queryFn: () => api.admin.aiConnections.list() })
  const connections: any[] = data?.data?.data || []

  const { data: platformData, isLoading: platformLoading } = useQuery({
    queryKey: ['admin-chatgpt-showcase'],
    queryFn: () => api.admin.chatgptShowcase.get(),
  })
  const platformConfig = platformData?.data?.data

  useEffect(() => {
    if (platformConfig?.chatgpt_app_url) {
      setPlatformAppUrl(platformConfig.chatgpt_app_url)
    }
  }, [platformConfig?.chatgpt_app_url])

  const platformSaveMutation = useMutation({
    mutationFn: (url: string) => api.admin.chatgptShowcase.update({
      mode: 'platform',
      chatgptAppUrl: url.trim() || null,
    }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['admin-chatgpt-showcase'] })
      setPlatformSaveError('')
    },
    onError: (err: any) => setPlatformSaveError(err.response?.data?.error?.message || err.response?.data?.message || 'Failed to save'),
  })

  const createMutation = useMutation({
    mutationFn: (d: any) => api.admin.aiConnections.create(d),
    onSuccess: (res) => {
      qc.invalidateQueries({ queryKey: ['admin-ai-connections'] })
      const d = res.data?.data
      if (d) setCredentials({ client_id: d.client_id, client_secret: d.client_secret, api_key: d.api_key, auth_username: d.auth_username, auth_password: d.auth_password })
      setShowCreate(false)
      setForm({ name: '', description: '' })
    },
    onError: (err: any) => {
      if (!err.response) {
        setFormError('Request blocked — sign in again as a platform admin (Admin role), or stop vendor impersonation if active.')
        return
      }
      setFormError(err.response?.data?.error?.message || err.response?.data?.message || 'Failed to create')
    },
  })

  const revokeMutation = useMutation({
    mutationFn: (clientId: string) => api.admin.aiConnections.revoke(clientId),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['admin-ai-connections'] }),
  })

  const copyToClipboard = (text: string, label: string) => {
    navigator.clipboard.writeText(text)
    setCopied(label)
    setTimeout(() => setCopied(null), 2000)
  }

  if (isLoading) return <div><PageHeader title="AI Connections" /><CardSkeleton /></div>

  return (
    <div>
      <PageHeader title="AI Connections" action={<button onClick={() => { setShowCreate(!showCreate); setCredentials(null) }}>New Global Connection</button>} />

      <div className="card" style={{ marginBottom: '1.5rem', padding: '0.75rem 1rem' }}>
        <p style={{ fontSize: '0.85rem', color: '#666', margin: 0 }}>
          <strong>Platform ChatGPT app</strong> — one OpenAI registration for all suppliers. Create a global connection below, register at{' '}
          <a href="https://platform.openai.com/apps-manage" target="_blank" rel="noopener noreferrer">platform.openai.com/apps-manage</a>
          , then paste the approved app URL. Per-vendor branded apps can be enabled later.
        </p>
      </div>

      <div className="card" style={{ marginBottom: '1.5rem' }}>
        <h3 style={{ marginTop: 0, marginBottom: '0.75rem' }}>Platform ChatGPT App URL</h3>
        {platformLoading ? (
          <CardSkeleton />
        ) : (
          <>
            <p style={{ fontSize: '0.85rem', color: '#666', marginTop: 0 }}>
              Mode: <strong>{platformConfig?.mode || 'platform'}</strong>
              {platformConfig?.has_platform_app ? ' · App configured' : ' · Not configured'}
            </p>
            <label style={{ display: 'block', marginBottom: '0.25rem', fontSize: '0.85rem' }}>Approved ChatGPT App URL</label>
            <input
              value={platformAppUrl}
              onChange={(e) => setPlatformAppUrl(e.target.value)}
              placeholder="https://chatgpt.com/g/..."
              style={{ width: '100%', padding: '0.5rem', border: '1px solid #ccc', borderRadius: '4px', marginBottom: '0.75rem' }}
            />
            {platformSaveError && <div style={{ color: '#dc3545', marginBottom: '0.75rem', fontSize: '0.85rem' }}>{platformSaveError}</div>}
            <div style={{ display: 'flex', gap: '0.75rem', flexWrap: 'wrap' }}>
              <button
                onClick={() => platformSaveMutation.mutate(platformAppUrl)}
                disabled={platformSaveMutation.isPending}
              >
                {platformSaveMutation.isPending ? 'Saving...' : 'Save platform app URL'}
              </button>
              {platformAppUrl && (
                <button
                  type="button"
                  onClick={() => copyToClipboard(platformAppUrl, 'platform-app-url')}
                  style={{ background: '#e5e7eb', color: '#333' }}
                >
                  {copied === 'platform-app-url' ? 'Copied' : 'Copy URL'}
                </button>
              )}
            </div>
            <p style={{ fontSize: '0.75rem', color: '#888', marginBottom: 0, marginTop: '0.75rem' }}>
              MCP: <code>https://mcp.tixxly.ai/mcp</code> · OAuth: global connection Client ID/Secret · Token method: client_secret_post
            </p>
          </>
        )}
      </div>

      <div className="card" style={{ marginBottom: '1.5rem', padding: '0.75rem 1rem' }}>
        <p style={{ fontSize: '0.85rem', color: '#666', margin: 0 }}>
          Global AI connections have access to <strong>all vendor catalogs</strong>. Use these for Tixxly's ChatGPT App OAuth credentials.
        </p>
      </div>

      {credentials && (
        <div className="card" style={{ marginBottom: '1.5rem', border: '2px solid #dc3545' }}>
          <p style={{ fontSize: '0.85rem', color: '#dc3545', fontWeight: 600, marginBottom: '1rem' }}>Copy all credentials now. They will not be shown again.</p>
          {[
            { label: 'API Key (x-api-key)', value: credentials.api_key },
            { label: 'OAuth Client ID', value: credentials.client_id },
            { label: 'OAuth Client Secret', value: credentials.client_secret },
            { label: 'Login Username', value: credentials.auth_username },
            { label: 'Login Password', value: credentials.auth_password },
          ].map(({ label, value }) => (
            <div key={label} style={{ marginBottom: '0.5rem' }}>
              <label style={{ display: 'block', fontSize: '0.75rem', color: '#666', marginBottom: '0.125rem' }}>{label}</label>
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', padding: '0.375rem 0.5rem', background: '#f9fafb', borderRadius: '4px', border: '1px solid #e5e7eb' }}>
                <code style={{ flex: 1, fontSize: '0.75rem', wordBreak: 'break-all' }}>{value}</code>
                <button onClick={() => copyToClipboard(value, label)} style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: '0.7rem', color: copied === label ? '#22c55e' : '#999' }}>
                  {copied === label ? 'Copied' : 'Copy'}
                </button>
              </div>
            </div>
          ))}
          <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: '1rem' }}>
            <button onClick={() => setCredentials(null)}>Done</button>
          </div>
        </div>
      )}

      {showCreate && (
        <div className="card" style={{ marginBottom: '1.5rem' }}>
          <h3 style={{ marginTop: 0 }}>New Global AI Connection</h3>
          {formError && <div style={{ color: '#dc3545', marginBottom: '1rem' }}>{formError}</div>}
          <div>
            <label style={{ display: 'block', marginBottom: '0.25rem', fontSize: '0.85rem' }}>Name *</label>
            <input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="e.g. Tixxly ChatGPT App"
              style={{ width: '100%', padding: '0.5rem', border: '1px solid #ccc', borderRadius: '4px' }} />
          </div>
          <div style={{ marginTop: '0.75rem' }}>
            <label style={{ display: 'block', marginBottom: '0.25rem', fontSize: '0.85rem' }}>Description</label>
            <input value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} placeholder="Optional"
              style={{ width: '100%', padding: '0.5rem', border: '1px solid #ccc', borderRadius: '4px' }} />
          </div>
          <div style={{ display: 'flex', gap: '0.75rem', marginTop: '1rem' }}>
            <button onClick={() => createMutation.mutate(form)} disabled={createMutation.isPending || !form.name.trim()}>
              {createMutation.isPending ? 'Creating...' : 'Create Connection'}
            </button>
            <button onClick={() => setShowCreate(false)} style={{ background: '#e5e7eb', color: '#333' }}>Cancel</button>
          </div>
        </div>
      )}

      <div style={{ overflowX: 'auto' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: '600px' }}>
          <thead>
            <tr style={{ borderBottom: '2px solid #e5e7eb', textAlign: 'left' }}>
              <th style={{ padding: '0.75rem' }}>Name</th>
              <th style={{ padding: '0.75rem' }}>Client ID</th>
              <th style={{ padding: '0.75rem' }}>Scope</th>
              <th style={{ padding: '0.75rem' }}>Status</th>
              <th style={{ padding: '0.75rem' }}>Created</th>
              <th style={{ padding: '0.75rem' }}>Actions</th>
            </tr>
          </thead>
          <tbody>
            {paginate(connections, connPage).map((c: any) => (
              <tr key={c.oauth_client_id} style={{ borderBottom: '1px solid #f3f4f6' }}>
                <td style={{ padding: '0.75rem', fontWeight: 500 }}>{c.name}</td>
                <td style={{ padding: '0.75rem' }}><code style={{ fontSize: '0.8rem' }}>{c.client_id}</code></td>
                <td style={{ padding: '0.75rem' }}>
                  <span style={{ display: 'inline-block', padding: '0.125rem 0.5rem', background: c.scope === 'global' ? '#dbeafe' : '#e0f2fe', borderRadius: '4px', fontSize: '0.75rem' }}>
                    {c.scope || 'vendor'}
                  </span>
                </td>
                <td style={{ padding: '0.75rem' }}><StatusBadge status={c.status} /></td>
                <td style={{ padding: '0.75rem', fontSize: '0.85rem', color: '#666' }}>{c.created_at ? new Date(c.created_at).toLocaleDateString() : '-'}</td>
                <td style={{ padding: '0.75rem' }}>
                  {c.status === 'active' && (
                    <button onClick={() => { if (confirm(`Revoke "${c.name}"? This cannot be undone.`)) revokeMutation.mutate(c.client_id) }}
                      style={{ fontSize: '0.8rem', padding: '0.25rem 0.75rem', background: '#dc3545', color: '#fff', border: 'none', borderRadius: '4px', cursor: 'pointer' }}>
                      Revoke
                    </button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {connections.length === 0 && <p style={{ textAlign: 'center', color: '#666', marginTop: '2rem' }}>No AI connections yet.</p>}
      <Pagination page={connPage} totalItems={connections.length} onPageChange={setConnPage} />
    </div>
  )
}

// ─── Vendor Detail ──────────────────────────────────────────────────────────

function SectionLabel({ children }: { children: React.ReactNode }) {
  return (
    <div style={{
      fontSize: '0.65rem', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.05em',
      color: 'var(--color-gray-500)', marginBottom: '0.5rem',
    }}>
      {children}
    </div>
  )
}

function InfoRow({ label, value, mono }: { label: string; value?: string | null; mono?: boolean }) {
  return (
    <tr style={{ borderBottom: '1px solid var(--color-gray-100)' }}>
      <td style={{ padding: '0.5rem 0.75rem', fontWeight: 500, color: 'var(--color-gray-500)', width: '40%', fontSize: '0.85rem' }}>{label}</td>
      <td style={{ padding: '0.5rem 0.75rem', fontSize: '0.85rem', fontFamily: mono ? 'monospace' : 'inherit' }}>{value || '--'}</td>
    </tr>
  )
}

function SummaryCard({ label, value, color, sub }: { label: string; value: string | number; color: string; sub?: string }) {
  return (
    <div style={{
      padding: '0.75rem 1rem', borderRadius: '8px', background: 'white',
      border: '1px solid var(--color-gray-200)', flex: 1, minWidth: '140px',
    }}>
      <div style={{ fontSize: '1.25rem', fontWeight: 700, color }}>{value}</div>
      <div style={{ fontSize: '0.75rem', color: 'var(--color-gray-500)' }}>{label}</div>
      {sub && <div style={{ fontSize: '0.7rem', color: 'var(--color-gray-400)', marginTop: '0.15rem' }}>{sub}</div>}
    </div>
  )
}

function OnboardingStep({ label, completed }: { label: string; completed: boolean }) {
  const bg = completed ? 'rgba(152,202,63,0.15)' : 'var(--color-gray-50)'
  const borderColor = completed ? '#98ca3f' : 'var(--color-gray-200)'
  const dotColor = completed ? '#98ca3f' : '#d1d5db'
  const statusText = completed ? 'Done' : 'Pending'
  const statusColor = completed ? '#059669' : 'var(--color-gray-400)'

  return (
    <div style={{
      display: 'flex', alignItems: 'center', justifyContent: 'space-between',
      padding: '0.5rem 0.75rem', borderRadius: '6px', background: bg,
      border: `1px solid ${borderColor}`,
    }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
        <span style={{ width: 8, height: 8, borderRadius: '50%', background: dotColor, flexShrink: 0 }} />
        <span style={{ fontSize: '0.85rem', fontWeight: 500 }}>{label}</span>
      </div>
      <span style={{ fontSize: '0.7rem', fontWeight: 600, color: statusColor }}>{statusText}</span>
    </div>
  )
}

function AdminVendorDetail() {
  const { vendorId } = useParams<{ vendorId: string }>()
  const navigate = useNavigate()
  const qc = useQueryClient()
  const { startImpersonation } = useAuthStore()

  // -- Data queries --
  const { data, isLoading } = useQuery({
    queryKey: ['admin-vendor', vendorId],
    queryFn: () => api.vendors.get(vendorId!),
    enabled: !!vendorId,
  })

  const { data: feePlansData } = useQuery({ queryKey: ['admin-fee-plans'], queryFn: () => api.admin.feePlans.list() })
  const feePlans = feePlansData?.data?.data?.plans || []

  const { data: stripeData } = useQuery({
    queryKey: ['admin-vendor-stripe', vendorId],
    queryFn: () => api.vendors.getStripeStatus(vendorId!),
    enabled: !!vendorId,
  })

  const { data: onboardingData } = useQuery({
    queryKey: ['admin-vendor-onboarding', vendorId],
    queryFn: () => api.vendors.getOnboardingStatus(vendorId!),
    enabled: !!vendorId,
  })

  const { data: bokunData } = useQuery({
    queryKey: ['admin-vendor-bokun', vendorId],
    queryFn: () => api.integrations.getBokunIntegration(vendorId!),
    enabled: !!vendorId,
    retry: false,
  })

  // getDomain triggers the server-side auto-sync from Vercel
  // (manageDomainConsolidated.ts:81-114) -- each GET re-checks domain
  // ownership + DNS and flips the status row if needed. Poll while pending
  // so the admin sees the flip without reloading.
  const domainQuery = useQuery({
    queryKey: ['admin-vendor-domain', vendorId],
    queryFn: () => api.vendors.getDomain(vendorId!),
    enabled: !!vendorId,
    refetchInterval: (q) => {
      const status = (q.state.data as any)?.data?.data?.customDomainStatus
      return status === 'pending' ? 15_000 : false
    },
  })
  const domain = (domainQuery.data as any)?.data?.data || null

  const vendor = data?.data?.data || data?.data || null
  // Prefer the freshly-synced domain status from getDomain over the cached
  // vendor row -- vendor.get can return stale data when DNS has just flipped.
  if (vendor && domain) {
    vendor.customDomain = domain.customDomain ?? vendor.customDomain
    vendor.customDomainStatus = domain.customDomainStatus ?? vendor.customDomainStatus
    vendor.customDomainConfig = domain.customDomainConfig ?? vendor.customDomainConfig
  }
  const contactInfo = typeof vendor?.contactInfo === 'string' ? JSON.parse(vendor.contactInfo) : (vendor?.contactInfo || {})
  const stripe = stripeData?.data?.data || stripeData?.data || null
  const onboarding = onboardingData?.data?.data || onboardingData?.data || null
  const bokun = bokunData?.data?.data || bokunData?.data || null

  // -- Mutations --
  const assignFeeMutation = useMutation({
    mutationFn: (feePlanId: number | null) => api.admin.vendors.assignFeePlan(vendorId!, feePlanId),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['admin-vendor', vendorId] }),
  })

  const activateMutation = useMutation({
    mutationFn: () => api.admin.vendors.activate(vendorId!),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['admin-vendor', vendorId] }),
    onError: (err: any) => alert(err?.response?.data?.error?.message || err?.message || 'Failed to activate vendor'),
  })

  const suspendMutation = useMutation({
    mutationFn: () => api.admin.vendors.suspend(vendorId!),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['admin-vendor', vendorId] }),
    onError: (err: any) => alert(err?.response?.data?.error?.message || err?.message || 'Failed to suspend vendor'),
  })

  const impersonateMutation = useMutation({
    mutationFn: () => api.admin.vendors.impersonate(vendorId!),
    onSuccess: (res: any) => {
      const data = res?.data?.data || res?.data
      if (data?.token && data?.user) {
        const u = data.user
        startImpersonation(
          {
            id: u.userId,
            email: u.email,
            name: [u.firstName, u.lastName].filter(Boolean).join(' ') || u.email,
            role: u.role,
            vendorId: String(u.tenantId),
            subscriptionTier: u.subscriptionTier,
          },
          data.token,
          u.vendorName || vendor?.company_name || 'Vendor',
        )
        navigate('/vendor')
      }
    },
  })

  // iter-8b: super-admin custom-domain controls (set / remove / lock).
  const [domainInput, setDomainInput] = useState('')
  const [domainError, setDomainError] = useState<string | null>(null)

  const invalidateDomain = () => {
    qc.invalidateQueries({ queryKey: ['admin-vendor', vendorId] })
    qc.invalidateQueries({ queryKey: ['admin-vendor-domain', vendorId] })
  }

  const setDomainMutation = useMutation({
    mutationFn: (value: string) => api.admin.vendors.setCustomDomain(vendorId!, value),
    onSuccess: () => {
      setDomainInput('')
      setDomainError(null)
      invalidateDomain()
    },
    onError: (err: any) => {
      const msg = err?.response?.data?.error?.message || err?.message || 'Failed to set domain'
      setDomainError(msg)
    },
  })

  const removeDomainMutation = useMutation({
    mutationFn: () => api.admin.vendors.removeCustomDomain(vendorId!),
    onSuccess: () => invalidateDomain(),
  })

  const lockDomainMutation = useMutation({
    mutationFn: (locked: boolean) => api.admin.vendors.lockDomain(vendorId!, locked),
    onSuccess: () => invalidateDomain(),
  })

  // iter-8a: storefront v3 status + upgrade
  const storefrontV3Query = useQuery({
    queryKey: ['admin-vendor-storefront-v3', vendorId],
    queryFn: () => api.admin.vendors.getStorefrontV3Status(vendorId!),
    enabled: !!vendorId,
    retry: false,
  })
  const storefrontV3 = (storefrontV3Query.data as any)?.data?.data || null

  const [v3PreviewOpen, setV3PreviewOpen] = useState(false)
  const [v3PreviewSeen, setV3PreviewSeen] = useState(false)
  const [v3Diff, setV3Diff] = useState<any>(null)

  const v3PreviewMutation = useMutation({
    mutationFn: () => api.admin.vendors.applyStorefrontV3(vendorId!, true),
    onSuccess: (res: any) => {
      const result = res?.data?.data || null
      setV3Diff(result)
      setV3PreviewOpen(true)
      setV3PreviewSeen(true)
    },
  })

  const v3ApplyMutation = useMutation({
    mutationFn: () => api.admin.vendors.applyStorefrontV3(vendorId!, false),
    onSuccess: (res: any) => {
      const result = res?.data?.data || null
      setV3Diff(result)
      setV3PreviewOpen(false)
      qc.invalidateQueries({ queryKey: ['admin-vendor-storefront-v3', vendorId] })
      qc.invalidateQueries({ queryKey: ['admin-vendor', vendorId] })
    },
  })

  if (isLoading) return <div><PageHeader title="Vendor Details" /><CardSkeleton /></div>
  if (!vendor) return <div><PageHeader title="Vendor Not Found" /><p>Vendor with ID {vendorId} was not found.</p></div>

  // -- Derived values --
  const onboardingSteps = (onboarding?.steps || []) as { id: string; completed: boolean }[]
  const completedCount = onboardingSteps.filter(s => s.completed).length
  const totalSteps = onboardingSteps.length
  const onboardingPct = totalSteps > 0 ? Math.round((completedCount / totalSteps) * 100) : 0
  const isOnboardingDone = onboarding?.completed || (totalSteps > 0 && onboardingPct === 100)

  const chargesEnabled = stripe?.chargesEnabled ?? false
  const payoutsEnabled = stripe?.payoutsEnabled ?? false
  const stripeConnected = stripe?.connected ?? !!vendor.stripeAccountId

  const bokunConnected = !!(bokun?.status === 'active' || bokun?.platform_merchant_id)
  const bokunMerchantId = bokun?.platform_merchant_id || null

  const stepLabels: Record<string, string> = {
    connect_provider: 'Connect Provider',
    sync_feeds: 'Sync Feeds',
    sync_experiences: 'Sync Experiences',
    connect_payment: 'Connect Payment',
    setup_ai_storefront: 'Setup AI Storefront',
  }

  // Storefront URL surfaces whichever surface the vendor has, even if the
  // custom domain status is still pending -- DNS often resolves before our
  // record flips to "active". The status badge in the card communicates
  // that distinction.
  const customDomainUrl = vendor.customDomain ? `https://${vendor.customDomain}` : null
  const hubUrl = vendor.username ? `https://stores.tixxly.ai/en/v/${vendor.username}` : null
  const storefrontUrl = customDomainUrl || hubUrl
  const isDomainPending = !!vendor.customDomain && vendor.customDomainStatus !== 'active'

  return (
    <div>
      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'center', gap: '1rem', marginBottom: '1.5rem' }}>
        <button onClick={() => navigate('/admin/vendors')} style={{ background: '#e5e7eb', color: '#333', padding: '0.25rem 0.75rem', fontSize: '0.85rem' }}>Back</button>
        <h2 style={{ margin: 0 }}>{vendor.companyName}</h2>
        <StatusBadge status={vendor.status} />
        <div style={{ marginLeft: 'auto' }}>
          <button
            onClick={() => impersonateMutation.mutate()}
            disabled={impersonateMutation.isPending}
            style={{
              fontSize: '0.8rem', padding: '0.4rem 1rem', borderRadius: '6px',
              border: '1px solid var(--color-sky-blue)', background: 'rgba(0,136,204,0.08)',
              color: 'var(--color-sky-blue)', cursor: 'pointer', fontWeight: 600,
            }}
          >
            {impersonateMutation.isPending ? 'Switching...' : 'View as Vendor'}
          </button>
        </div>
      </div>

      {/* Summary row */}
      <div style={{ display: 'flex', gap: '1rem', marginBottom: '1.5rem', flexWrap: 'wrap' }}>
        <SummaryCard
          label="Onboarding"
          value={isOnboardingDone ? 'Complete' : `${onboardingPct}%`}
          color={isOnboardingDone ? '#059669' : 'var(--color-sky-blue)'}
          sub={`${completedCount}/${totalSteps} steps`}
        />
        <SummaryCard
          label="Stripe Connect"
          value={!stripeConnected ? 'Not Connected' : chargesEnabled && payoutsEnabled ? 'Active' : 'Incomplete'}
          color={chargesEnabled && payoutsEnabled ? '#059669' : !stripeConnected ? '#dc3545' : '#b45309'}
        />
        <SummaryCard
          label="Bokun"
          value={bokunConnected ? 'Connected' : 'Not Connected'}
          color={bokunConnected ? '#059669' : 'var(--color-gray-400)'}
          sub={bokunMerchantId ? `ID: ${bokunMerchantId}` : undefined}
        />
        <SummaryCard
          label="Storefront"
          value={
            !storefrontUrl
              ? 'Not Set Up'
              : isDomainPending
                ? 'Pending'
                : 'Live'
          }
          color={
            !storefrontUrl
              ? 'var(--color-gray-400)'
              : isDomainPending
                ? '#b45309'
                : '#059669'
          }
          sub={vendor.customDomain || (vendor.username ? `@${vendor.username}` : undefined)}
        />
      </div>

      {/* Main grid */}
      <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr', gap: '1.5rem' }}>
        {/* Left column */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: '1.5rem' }}>
          {/* Vendor Information */}
          <div className="card">
            <h3 style={{ marginTop: 0 }}>Vendor Information</h3>
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <tbody>
                <InfoRow label="Company Name" value={vendor.companyName} />
                <InfoRow label="Legal Name" value={vendor.legalName} />
                <InfoRow label="Business Type" value={vendor.businessType} />
                <InfoRow label="Website" value={vendor.website} />
                <InfoRow label="Tax ID" value={vendor.taxId} mono />
                <InfoRow label="Registration Number" value={vendor.registrationNumber} mono />
                <InfoRow label="Email" value={contactInfo.email} />
                <InfoRow label="Phone" value={contactInfo.phone} />
                <InfoRow label="Created" value={vendor.createdAt ? new Date(vendor.createdAt).toLocaleDateString() : undefined} />
                <InfoRow label="Updated" value={vendor.updatedAt ? new Date(vendor.updatedAt).toLocaleDateString() : undefined} />
              </tbody>
            </table>

            {contactInfo.address && (
              <div style={{ marginTop: '1rem', padding: '0.75rem', background: 'var(--color-gray-50)', borderRadius: '6px' }}>
                <SectionLabel>Address</SectionLabel>
                <p style={{ margin: 0, fontSize: '0.85rem', color: 'var(--color-gray-700)' }}>
                  {contactInfo.address.street && <>{contactInfo.address.street}<br /></>}
                  {contactInfo.address.city}{contactInfo.address.state ? `, ${contactInfo.address.state}` : ''}
                  {contactInfo.address.postal_code ? ` ${contactInfo.address.postal_code}` : ''}<br />
                  {contactInfo.address.country}
                </p>
              </div>
            )}
          </div>

          {/* Selling currency (admin can switch a vendor, e.g. Los Patos to EUR) */}
          <CurrencySection vendorId={vendorId} />

          {/* Onboarding Progress */}
          <div className="card">
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.75rem' }}>
              <h3 style={{ margin: 0 }}>Onboarding Progress</h3>
              <span style={{ fontSize: '0.85rem', fontWeight: 600, color: isOnboardingDone ? '#059669' : 'var(--color-sky-blue)' }}>
                {onboardingPct}%
              </span>
            </div>

            {/* Progress bar */}
            <div style={{ height: '8px', background: 'var(--color-gray-200)', borderRadius: '4px', overflow: 'hidden', marginBottom: '1rem' }}>
              <div style={{
                width: `${onboardingPct}%`, height: '100%', borderRadius: '4px',
                background: isOnboardingDone ? '#98ca3f' : 'var(--color-sky-blue)', transition: 'width 0.3s',
              }} />
            </div>

            <div style={{ display: 'flex', flexDirection: 'column', gap: '0.4rem' }}>
              {onboardingSteps.map(step => (
                <OnboardingStep
                  key={step.id}
                  label={stepLabels[step.id] || step.id}
                  completed={step.completed}
                />
              ))}
            </div>
          </div>

          {/* Stripe Connect Status */}
          <div className="card">
            <h3 style={{ marginTop: 0 }}>Stripe Connect Status</h3>

            {!stripeConnected ? (
              <div style={{
                padding: '1rem', background: 'var(--color-gray-50)', borderRadius: '6px',
                textAlign: 'center', color: 'var(--color-gray-500)', fontSize: '0.9rem',
              }}>
                Stripe Connect not linked. Vendor has not started onboarding.
              </div>
            ) : (
              <>
                <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                  <tbody>
                    <InfoRow label="Account ID" value={vendor.stripeAccountId} mono />
                    <InfoRow label="Onboarding Complete" value={vendor.stripeOnboardingComplete ? 'Yes' : 'No'} />
                    <tr style={{ borderBottom: '1px solid var(--color-gray-100)' }}>
                      <td style={{ padding: '0.5rem 0.75rem', fontWeight: 500, color: 'var(--color-gray-500)', width: '40%', fontSize: '0.85rem' }}>Can Charge</td>
                      <td style={{ padding: '0.5rem 0.75rem' }}>
                        <Indicator ok={chargesEnabled} label={chargesEnabled ? 'Yes -- charges enabled' : 'No -- charges disabled'} />
                      </td>
                    </tr>
                    <tr style={{ borderBottom: '1px solid var(--color-gray-100)' }}>
                      <td style={{ padding: '0.5rem 0.75rem', fontWeight: 500, color: 'var(--color-gray-500)', width: '40%', fontSize: '0.85rem' }}>Can Receive Payouts</td>
                      <td style={{ padding: '0.5rem 0.75rem' }}>
                        <Indicator ok={payoutsEnabled} label={payoutsEnabled ? 'Yes -- payouts enabled' : 'No -- payouts disabled'} />
                      </td>
                    </tr>
                    {stripe?.detailsSubmitted !== undefined && (
                      <InfoRow label="Details Submitted" value={stripe.detailsSubmitted ? 'Yes' : 'No'} />
                    )}
                  </tbody>
                </table>

                {/* Requirements */}
                {stripe?.requirements && (stripe.requirements.currentlyDue?.length > 0 || stripe.requirements.pastDue?.length > 0) && (
                  <div style={{ marginTop: '0.75rem' }}>
                    {stripe.requirements.pastDue?.length > 0 && (
                      <div style={{
                        padding: '0.5rem 0.75rem', background: 'rgba(220,53,69,0.08)', border: '1px solid rgba(220,53,69,0.2)',
                        borderRadius: '6px', marginBottom: '0.5rem',
                      }}>
                        <SectionLabel>Past Due Requirements</SectionLabel>
                        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.25rem' }}>
                          {stripe.requirements.pastDue.map((r: string) => (
                            <span key={r} style={{
                              padding: '0.15rem 0.5rem', background: 'rgba(220,53,69,0.1)', borderRadius: '4px',
                              fontSize: '0.75rem', color: '#dc3545',
                            }}>{r}</span>
                          ))}
                        </div>
                      </div>
                    )}
                    {stripe.requirements.currentlyDue?.length > 0 && (
                      <div style={{
                        padding: '0.5rem 0.75rem', background: 'rgba(255,193,7,0.08)', border: '1px solid rgba(255,193,7,0.2)',
                        borderRadius: '6px',
                      }}>
                        <SectionLabel>Currently Due</SectionLabel>
                        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.25rem' }}>
                          {stripe.requirements.currentlyDue.map((r: string) => (
                            <span key={r} style={{
                              padding: '0.15rem 0.5rem', background: 'rgba(255,193,7,0.1)', borderRadius: '4px',
                              fontSize: '0.75rem', color: '#b45309',
                            }}>{r}</span>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </>
            )}
          </div>
        </div>

        {/* Right column */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
          {/* Actions */}
          <div className="card">
            <h3 style={{ marginTop: 0 }}>Actions</h3>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
              <PermissionGate permission="vendors:activate">
                {vendor.status !== 'active' && (
                  <button onClick={() => activateMutation.mutate()} disabled={activateMutation.isPending} style={{ width: '100%' }}>
                    {activateMutation.isPending ? 'Activating...' : 'Activate Vendor'}
                  </button>
                )}
              </PermissionGate>
              <PermissionGate permission="vendors:suspend">
                {vendor.status !== 'suspended' && (
                  <button onClick={() => suspendMutation.mutate()} disabled={suspendMutation.isPending}
                    style={{ width: '100%', background: '#dc3545', color: '#fff', border: 'none', borderRadius: '4px', padding: '0.5rem', cursor: 'pointer' }}>
                    {suspendMutation.isPending ? 'Suspending...' : 'Suspend Vendor'}
                  </button>
                )}
              </PermissionGate>
              <ThingsToDoCsvExportButton
                vendorId={vendorId}
                vendorUsername={vendor.username}
                style={{ width: '100%' }}
              />
            </div>
          </div>

          {/* Bokun Integration */}
          <div className="card">
            <h3 style={{ marginTop: 0 }}>Bokun Integration</h3>
            {bokunConnected ? (
              <div>
                <Indicator ok={true} label="Connected" />
                <table style={{ width: '100%', borderCollapse: 'collapse', marginTop: '0.5rem' }}>
                  <tbody>
                    {bokunMerchantId && <InfoRow label="Merchant ID" value={bokunMerchantId} mono />}
                    {bokun?.connected_at && <InfoRow label="Connected" value={new Date(bokun.connected_at).toLocaleDateString()} />}
                  </tbody>
                </table>
              </div>
            ) : (
              <div style={{
                padding: '0.75rem', background: 'var(--color-gray-50)', borderRadius: '6px',
                fontSize: '0.85rem', color: 'var(--color-gray-500)',
              }}>
                <Indicator ok={false} label="Not connected" />
                <p style={{ margin: '0.5rem 0 0', fontSize: '0.8rem' }}>Vendor has not linked a Bokun account.</p>
              </div>
            )}
          </div>

          {/* Storefront */}
          <div className="card">
            <h3 style={{ marginTop: 0 }}>Storefront</h3>
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <tbody>
                <InfoRow label="Username" value={vendor.username ? `@${vendor.username}` : undefined} />
                <InfoRow label="Custom Domain" value={vendor.customDomain} />
                {vendor.customDomain && (
                  <tr style={{ borderBottom: '1px solid var(--color-gray-100)' }}>
                    <td style={{ padding: '0.5rem 0.75rem', fontWeight: 500, color: 'var(--color-gray-500)', width: '40%', fontSize: '0.85rem' }}>Domain Status</td>
                    <td style={{ padding: '0.5rem 0.75rem', display: 'flex', alignItems: 'center', gap: '0.5rem', flexWrap: 'wrap' }}>
                      <StatusBadge status={vendor.customDomainStatus || 'pending'} />
                      {isDomainPending && (
                        <button
                          type="button"
                          onClick={() => domainQuery.refetch()}
                          disabled={domainQuery.isFetching}
                          title="Re-check Vercel ownership and DNS"
                          style={{
                            fontSize: '0.7rem', padding: '0.25rem 0.55rem',
                            border: '1px solid #1a2e44', borderRadius: '4px',
                            background: 'var(--color-pure-white)', color: '#1a2e44',
                            cursor: domainQuery.isFetching ? 'wait' : 'pointer',
                            opacity: domainQuery.isFetching ? 0.6 : 1,
                          }}
                        >
                          {domainQuery.isFetching ? 'Checking...' : 'Verify now'}
                        </button>
                      )}
                      <PermissionGate permission="vendors:activate">
                        {vendor.customDomainStatus === 'locked' ? (
                          <button
                            type="button"
                            onClick={() => lockDomainMutation.mutate(false)}
                            disabled={lockDomainMutation.isPending}
                            title="Unlock domain to allow further edits"
                            style={{
                              fontSize: '0.7rem', padding: '0.25rem 0.55rem',
                              border: '1px solid #1a2e44', borderRadius: '4px',
                              background: 'var(--color-pure-white)', color: '#1a2e44',
                              cursor: lockDomainMutation.isPending ? 'wait' : 'pointer',
                              opacity: lockDomainMutation.isPending ? 0.6 : 1,
                            }}
                          >
                            {lockDomainMutation.isPending ? 'Unlocking...' : 'Unlock'}
                          </button>
                        ) : (
                          <button
                            type="button"
                            onClick={() => lockDomainMutation.mutate(true)}
                            disabled={lockDomainMutation.isPending}
                            title="Lock domain to prevent vendor edits"
                            style={{
                              fontSize: '0.7rem', padding: '0.25rem 0.55rem',
                              border: '1px solid #1a2e44', borderRadius: '4px',
                              background: 'var(--color-pure-white)', color: '#1a2e44',
                              cursor: lockDomainMutation.isPending ? 'wait' : 'pointer',
                              opacity: lockDomainMutation.isPending ? 0.6 : 1,
                            }}
                          >
                            {lockDomainMutation.isPending ? 'Locking...' : 'Lock'}
                          </button>
                        )}
                      </PermissionGate>
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
            {storefrontUrl ? (
              <>
                <a href={storefrontUrl} target="_blank" rel="noopener noreferrer"
                  style={{
                    display: 'block', marginTop: '0.75rem', padding: '0.4rem 0.75rem',
                    background: 'rgba(152,202,63,0.1)', border: '1px solid #98ca3f', borderRadius: '6px',
                    fontSize: '0.8rem', color: '#1a2e44', textDecoration: 'none', textAlign: 'center',
                  }}>
                  Visit Storefront
                </a>
                {isDomainPending && (
                  <DomainPendingNote
                    config={vendor.customDomainConfig}
                    status={vendor.customDomainStatus || 'pending'}
                  />
                )}
              </>
            ) : (
              <p style={{ margin: '0.5rem 0 0', fontSize: '0.8rem', color: 'var(--color-gray-400)' }}>
                No username or custom domain configured.
              </p>
            )}

            {/* iter-8a: storefront v3 version + upgrade panel */}
            {storefrontV3 && (
              <div style={{ marginTop: '1rem', paddingTop: '0.75rem', borderTop: '1px solid var(--color-gray-100)' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.5rem' }}>
                  <span style={{ fontSize: '0.85rem', fontWeight: 500, color: 'var(--color-gray-500)' }}>Storefront version</span>
                  {storefrontV3.version === 'V3' ? (
                    <span style={{
                      padding: '0.2rem 0.6rem', borderRadius: '4px', fontSize: '0.7rem', fontWeight: 600,
                      background: 'rgba(152,202,63,0.15)', color: '#1a2e44', border: '1px solid #98ca3f',
                    }}>V3 live</span>
                  ) : (
                    <span style={{
                      padding: '0.2rem 0.6rem', borderRadius: '4px', fontSize: '0.7rem', fontWeight: 600,
                      background: '#fff3cd', color: '#856404', border: '1px solid #ffc107',
                    }}>V2</span>
                  )}
                  <span style={{ marginLeft: 'auto', fontSize: '0.75rem', color: 'var(--color-gray-400)' }}>
                    {storefrontV3.populated_vendor_fields} / {storefrontV3.total_vendor_fields} fields populated
                  </span>
                </div>

                {storefrontV3.version === 'V2' && (
                  <div style={{
                    padding: '0.75rem', background: 'rgba(255,243,205,0.4)', border: '1px solid #ffc107',
                    borderRadius: '6px', fontSize: '0.8rem',
                  }}>
                    <p style={{ margin: '0 0 0.5rem', color: '#1a2e44' }}>
                      Bokun source data covers <strong>{storefrontV3.bokun_available_fields}</strong> missing field(s).
                      Apply will only fill NULL fields - existing vendor edits are never overwritten.
                    </p>
                    <p style={{ margin: '0 0 0.75rem', color: 'var(--color-gray-500)', fontSize: '0.75rem' }}>
                      Per-product v3 extensions: {storefrontV3.products_with_v3} / {storefrontV3.total_products} products already populated.
                    </p>
                    <div style={{ display: 'flex', gap: '0.5rem' }}>
                      <button
                        type="button"
                        onClick={() => v3PreviewMutation.mutate()}
                        disabled={v3PreviewMutation.isPending}
                        style={{
                          fontSize: '0.75rem', padding: '0.35rem 0.75rem',
                          border: '1px solid #1a2e44', borderRadius: '4px',
                          background: 'var(--color-pure-white)', color: '#1a2e44',
                          cursor: v3PreviewMutation.isPending ? 'wait' : 'pointer',
                          opacity: v3PreviewMutation.isPending ? 0.6 : 1,
                        }}
                      >
                        {v3PreviewMutation.isPending ? 'Loading...' : 'Preview changes'}
                      </button>
                      <button
                        type="button"
                        onClick={() => {
                          if (window.confirm('Apply the v3 backfill for this vendor? Only NULL fields will be written.')) {
                            v3ApplyMutation.mutate()
                          }
                        }}
                        disabled={!v3PreviewSeen || v3ApplyMutation.isPending}
                        title={v3PreviewSeen ? 'Apply v3 backfill' : 'Open Preview first so you can see what will be written'}
                        style={{
                          fontSize: '0.75rem', padding: '0.35rem 0.75rem',
                          border: '1px solid #98ca3f', borderRadius: '4px',
                          background: v3PreviewSeen ? '#98ca3f' : 'var(--color-gray-100)',
                          color: v3PreviewSeen ? '#1a2e44' : 'var(--color-gray-400)',
                          cursor: !v3PreviewSeen || v3ApplyMutation.isPending ? 'not-allowed' : 'pointer',
                          fontWeight: 600,
                        }}
                      >
                        {v3ApplyMutation.isPending ? 'Applying...' : 'Upgrade to V3'}
                      </button>
                    </div>
                    {v3ApplyMutation.isError && (
                      <p style={{ margin: '0.5rem 0 0', fontSize: '0.75rem', color: '#721c24' }}>
                        Apply failed. Check the network tab for details.
                      </p>
                    )}
                    {v3ApplyMutation.isSuccess && v3Diff && (
                      <p style={{ margin: '0.5rem 0 0', fontSize: '0.75rem', color: '#059669' }}>
                        Wrote {v3Diff.vendor_changes?.length || 0} vendor field(s), updated {v3Diff.products_updated || 0} product(s).
                      </p>
                    )}
                  </div>
                )}
              </div>
            )}

            {/* iter-8b: super-admin custom-domain set / remove controls. */}
            <PermissionGate permission="vendors:activate">
              <div style={{ marginTop: '0.85rem', paddingTop: '0.75rem', borderTop: '1px solid var(--color-gray-100)' }}>
                {vendor.customDomain ? (
                  <div>
                    <button
                      type="button"
                      onClick={() => {
                        if (window.confirm(`Remove ${vendor.customDomain} from this vendor? The storefront will fall back to the @username surface.`)) {
                          removeDomainMutation.mutate()
                        }
                      }}
                      disabled={removeDomainMutation.isPending || vendor.customDomainStatus === 'locked'}
                      title={vendor.customDomainStatus === 'locked' ? 'Unlock the domain before removing it' : 'Remove the custom domain from this vendor'}
                      style={{
                        fontSize: '0.75rem', padding: '0.35rem 0.75rem',
                        border: '1px solid #c0392b', borderRadius: '4px',
                        background: 'var(--color-pure-white)', color: '#c0392b',
                        cursor: removeDomainMutation.isPending || vendor.customDomainStatus === 'locked' ? 'not-allowed' : 'pointer',
                        opacity: removeDomainMutation.isPending || vendor.customDomainStatus === 'locked' ? 0.5 : 1,
                      }}
                    >
                      {removeDomainMutation.isPending ? 'Removing...' : 'Remove custom domain'}
                    </button>
                    {removeDomainMutation.isError && (
                      <p style={{ margin: '0.4rem 0 0', fontSize: '0.75rem', color: '#c0392b' }}>
                        {(removeDomainMutation.error as any)?.response?.data?.error?.message || 'Failed to remove domain'}
                      </p>
                    )}
                  </div>
                ) : (
                  <div>
                    <label style={{ display: 'block', fontSize: '0.75rem', fontWeight: 500, color: 'var(--color-gray-500)', marginBottom: '0.35rem' }}>
                      Connect a custom domain
                    </label>
                    <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'stretch' }}>
                      <input
                        type="text"
                        value={domainInput}
                        onChange={(e) => { setDomainInput(e.target.value); setDomainError(null) }}
                        placeholder="ai.example.com"
                        disabled={!vendor.username || setDomainMutation.isPending}
                        style={{
                          flex: 1, padding: '0.4rem 0.6rem', fontSize: '0.8rem',
                          border: '1px solid var(--color-gray-200)', borderRadius: '4px',
                        }}
                      />
                      <button
                        type="button"
                        onClick={() => {
                          const value = domainInput.trim().toLowerCase()
                          if (!value) { setDomainError('Enter a domain'); return }
                          setDomainMutation.mutate(value)
                        }}
                        disabled={!vendor.username || setDomainMutation.isPending || !domainInput.trim()}
                        style={{
                          fontSize: '0.75rem', padding: '0.4rem 0.85rem',
                          border: '1px solid #1a2e44', borderRadius: '4px',
                          background: '#1a2e44', color: 'var(--color-pure-white)',
                          cursor: setDomainMutation.isPending ? 'wait' : 'pointer',
                          opacity: !vendor.username || setDomainMutation.isPending || !domainInput.trim() ? 0.5 : 1,
                          whiteSpace: 'nowrap',
                        }}
                      >
                        {setDomainMutation.isPending ? 'Connecting...' : 'Connect domain'}
                      </button>
                    </div>
                    {!vendor.username && (
                      <p style={{ margin: '0.4rem 0 0', fontSize: '0.7rem', color: 'var(--color-gray-400)' }}>
                        Set a username on this vendor before connecting a custom domain.
                      </p>
                    )}
                    <p style={{ margin: '0.4rem 0 0', fontSize: '0.7rem', color: 'var(--color-gray-400)' }}>
                      Subdomains only (e.g. ai.example.com). Apex domains are not accepted.
                    </p>
                    {domainError && (
                      <p style={{ margin: '0.4rem 0 0', fontSize: '0.75rem', color: '#c0392b' }}>
                        {domainError}
                      </p>
                    )}
                  </div>
                )}
              </div>
            </PermissionGate>
          </div>

          {/* iter-8a: storefront v3 diff modal */}
          {v3PreviewOpen && v3Diff && (
            <Modal isOpen={v3PreviewOpen} onClose={() => setV3PreviewOpen(false)} title="Storefront V3 - Preview changes">
              <div style={{ fontSize: '0.85rem' }}>
                <p style={{ margin: '0 0 0.75rem', color: 'var(--color-gray-500)' }}>
                  Dry-run only. Nothing has been written yet. Close this modal and click <strong>Upgrade to V3</strong> to apply.
                </p>

                <h4 style={{ margin: '1rem 0 0.5rem' }}>
                  Vendor-level fields ({v3Diff.vendor_changes?.length || 0})
                </h4>
                {(!v3Diff.vendor_changes || v3Diff.vendor_changes.length === 0) ? (
                  <p style={{ margin: 0, color: 'var(--color-gray-400)', fontStyle: 'italic' }}>No vendor-level changes - either no Bokun signal or all fields already populated.</p>
                ) : (
                  <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.8rem' }}>
                    <thead>
                      <tr style={{ borderBottom: '1px solid var(--color-gray-200)' }}>
                        <th style={{ textAlign: 'left', padding: '0.4rem' }}>Field</th>
                        <th style={{ textAlign: 'left', padding: '0.4rem' }}>From</th>
                        <th style={{ textAlign: 'left', padding: '0.4rem' }}>To (preview)</th>
                      </tr>
                    </thead>
                    <tbody>
                      {v3Diff.vendor_changes.map((c: any, i: number) => (
                        <tr key={i} style={{ borderBottom: '1px solid var(--color-gray-100)', verticalAlign: 'top' }}>
                          <td style={{ padding: '0.4rem', fontWeight: 600 }}>{c.field}</td>
                          <td style={{ padding: '0.4rem', color: 'var(--color-gray-400)', fontFamily: 'monospace', fontSize: '0.7rem' }}>
                            {c.from == null || c.from === '' ? 'NULL' : (
                              typeof c.from === 'object' ? JSON.stringify(c.from).slice(0, 80) : String(c.from).slice(0, 80)
                            )}
                          </td>
                          <td style={{ padding: '0.4rem', fontFamily: 'monospace', fontSize: '0.7rem' }}>
                            {typeof c.to === 'object' ? JSON.stringify(c.to).slice(0, 120) : String(c.to).slice(0, 120)}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}

                <h4 style={{ margin: '1.5rem 0 0.5rem' }}>
                  Per-product extensions ({v3Diff.products_updated || 0} products would update)
                </h4>
                <p style={{ margin: '0 0 0.5rem', color: 'var(--color-gray-500)', fontSize: '0.75rem' }}>
                  No signal: {v3Diff.products_skipped_no_signal || 0}. Already populated: {v3Diff.products_skipped_already_populated || 0}.
                </p>
                {v3Diff.product_changes && v3Diff.product_changes.length > 0 && (
                  <details>
                    <summary style={{ cursor: 'pointer', fontSize: '0.8rem' }}>Show product list</summary>
                    <ul style={{ marginTop: '0.5rem', fontSize: '0.75rem', fontFamily: 'monospace' }}>
                      {v3Diff.product_changes.slice(0, 50).map((p: any) => (
                        <li key={p.product_id}>#{p.product_id}: {p.fields_added.join(', ')}</li>
                      ))}
                      {v3Diff.product_changes.length > 50 && (
                        <li>... and {v3Diff.product_changes.length - 50} more</li>
                      )}
                    </ul>
                  </details>
                )}

                <div style={{ marginTop: '1rem', display: 'flex', justifyContent: 'flex-end', gap: '0.5rem' }}>
                  <button type="button" onClick={() => setV3PreviewOpen(false)}
                    style={{ fontSize: '0.8rem', padding: '0.4rem 0.9rem', background: 'var(--color-gray-100)', color: '#1a2e44', border: '1px solid var(--color-gray-200)', borderRadius: '4px' }}>
                    Close
                  </button>
                </div>
              </div>
            </Modal>
          )}

          {/* Fee Plan */}
          <PermissionGate permission="fees:assign">
            <div className="card">
              <h3 style={{ marginTop: 0 }}>Fee Plan</h3>
              <select
                value={vendor.fee_plan_id || ''}
                onChange={(e) => assignFeeMutation.mutate(e.target.value ? Number(e.target.value) : null)}
                style={{ width: '100%', padding: '0.5rem' }}
              >
                <option value="">No fee plan (use default)</option>
                {feePlans.map((p: any) => (
                  <option key={p.fee_plan_id} value={p.fee_plan_id}>
                    {p.name} ({p.percentage_fee}%{p.fixed_fee > 0 ? ` + ${p.currency} ${p.fixed_fee}` : ''})
                    {p.is_default ? ' [Default]' : ''}
                  </option>
                ))}
              </select>
              {(() => {
                if (vendor.fee_plan_id) {
                  const fp = feePlans.find((p: any) => p.fee_plan_id === vendor.fee_plan_id)
                  return fp ? (
                    <p style={{ margin: '0.5rem 0 0', fontSize: '0.8rem', color: '#059669' }}>
                      Active: {fp.name} ({fp.percentage_fee}%) -- assigned directly to vendor
                    </p>
                  ) : null
                }
                const subPlans = qc.getQueryData<any>(['admin-plans'])
                const tierPlans = subPlans?.data?.data?.plans || subPlans?.data?.plans || []
                const tierPlan = vendor.plan_id ? tierPlans.find((sp: any) => sp.plan_id === vendor.plan_id) : null
                if (tierPlan?.fee_plan_id) {
                  const fp = feePlans.find((p: any) => p.fee_plan_id === tierPlan.fee_plan_id)
                  return fp ? (
                    <p style={{ margin: '0.5rem 0 0', fontSize: '0.8rem', color: '#2563eb' }}>
                      Active: {fp.name} ({fp.percentage_fee}%) -- from {tierPlan.name} subscription
                    </p>
                  ) : null
                }
                const defaultPlan = feePlans.find((p: any) => p.is_default)
                if (defaultPlan) {
                  return (
                    <p style={{ margin: '0.5rem 0 0', fontSize: '0.8rem', color: '#666' }}>
                      Active: {defaultPlan.name} ({defaultPlan.percentage_fee}%) -- system default
                    </p>
                  )
                }
                return (
                  <p style={{ margin: '0.5rem 0 0', fontSize: '0.8rem', color: '#666' }}>
                    Active: 7.5% -- hardcoded fallback
                  </p>
                )
              })()}
            </div>
          </PermissionGate>

          <VendorFeedKeyCard vendorId={vendorId!} />
          <VendorSearchConsoleCard vendorId={vendorId!} vendor={vendor} />
        </div>
      </div>
    </div>
  )
}

// ─── Search Console Verification Card ───────────────────────────────────────
//
// Tixxly admins use this to drop the GSC URL-prefix verification token onto
// a specific vendor's storefront. The storefront layout reads
// vendor.seo.google_site_verification and emits the meta tag on the next
// page load -- no deploy needed.

const GSC_TOKEN_PATTERN = /^[A-Za-z0-9_-]{16,128}$/

// Surface what Vercel is waiting on when a custom domain is still pending.
// If verification[] has entries, the vendor needs to add those TXT records
// on DNS before Vercel can confirm ownership. If dns_verified is false the
// CNAME/A record isn't pointing at Vercel yet.
function DomainPendingNote({ config, status }: { config: any; status: string }) {
  const verification = Array.isArray(config?.verification) ? config.verification : []
  const dnsVerified = config?.dns_verified === true

  return (
    <div style={{ marginTop: '0.5rem', padding: '0.5rem 0.75rem', background: '#fffbeb', border: '1px solid #fbbf24', borderRadius: '6px' }}>
      <p style={{ margin: 0, fontSize: '0.75rem', color: '#92400e' }}>
        Status is <code>{status}</code>. Click <strong>Verify now</strong> to re-check Vercel.
      </p>
      <ul style={{ margin: '0.4rem 0 0 1rem', padding: 0, fontSize: '0.7rem', color: '#92400e' }}>
        <li>DNS points at Vercel: <strong>{dnsVerified ? 'yes' : 'no'}</strong></li>
        <li>Ownership verified: <strong>{verification.length === 0 ? 'yes' : 'no, TXT record(s) missing'}</strong></li>
      </ul>
      {verification.length > 0 && (
        <div style={{ marginTop: '0.5rem' }}>
          <p style={{ margin: '0 0 0.25rem', fontSize: '0.7rem', fontWeight: 600, color: '#92400e' }}>
            The vendor needs to add these on their DNS provider:
          </p>
          {verification.map((v: any, i: number) => (
            <div key={i} style={{ fontSize: '0.7rem', color: '#7c2d12', fontFamily: 'ui-monospace, Menlo, monospace', display: 'grid', gridTemplateColumns: '60px 1fr', columnGap: '0.5rem' }}>
              <span>Type</span><span>{v.type}</span>
              <span>Name</span><span style={{ wordBreak: 'break-all' }}>{v.domain}</span>
              <span>Value</span><span style={{ wordBreak: 'break-all' }}>{v.value}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  )
}

function VendorSearchConsoleCard({ vendorId, vendor }: { vendorId: string; vendor: any }) {
  const qc = useQueryClient()
  const savedGoogle: string = vendor?.seo?.google_site_verification ?? ''
  const savedBing: string = vendor?.seo?.bing_site_verification ?? ''

  const [googleToken, setGoogleToken] = useState(savedGoogle)
  const [bingToken, setBingToken] = useState(savedBing)
  const [error, setError] = useState<string | null>(null)
  const [success, setSuccess] = useState<string | null>(null)

  // Keep the inputs synced when the cached vendor record refreshes.
  useEffect(() => { setGoogleToken(savedGoogle) }, [savedGoogle])
  useEffect(() => { setBingToken(savedBing) }, [savedBing])

  const mutation = useMutation({
    mutationFn: (payload: { googleSiteVerification: string; bingSiteVerification: string }) =>
      api.vendors.updateSEO(vendorId, payload),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['admin-vendor', vendorId] })
      setSuccess('Saved. Click "Verify" in Search Console.')
      setTimeout(() => setSuccess(null), 5000)
    },
    onError: (err: any) => {
      setError(err?.response?.data?.error?.message || err?.message || 'Failed to save')
    },
  })

  // Operators usually paste the whole <meta ... content="X">. Pull X out.
  const extractToken = (input: string): string => {
    const m = input.match(/content=["']([^"']+)["']/)
    return (m?.[1] || input).trim()
  }

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    setError(null)
    setSuccess(null)
    const g = googleToken.trim()
    const b = bingToken.trim()
    if (g && !GSC_TOKEN_PATTERN.test(g)) {
      setError('Google token looks malformed -- paste only the content="..." value.')
      return
    }
    if (b && !GSC_TOKEN_PATTERN.test(b)) {
      setError('Bing token looks malformed -- paste only the content="..." value.')
      return
    }
    mutation.mutate({ googleSiteVerification: g, bingSiteVerification: b })
  }

  const storefrontUrl = vendor?.custom_domain
    ? `https://${vendor.custom_domain}`
    : vendor?.username ? `https://stores.tixxly.ai/en/v/${vendor.username}` : null

  return (
    <div className="card">
      <h3 style={{ marginTop: 0 }}>Search Console Verification</h3>
      <p style={{ fontSize: '0.8rem', color: '#666', marginBottom: '0.75rem' }}>
        Drop the GSC URL-prefix token here.
        {storefrontUrl && <> Generate it for <code>{storefrontUrl}</code> at https://search.google.com/search-console.</>}
        {' '}Saving updates the storefront on the next page load -- no deploy.
      </p>

      {savedGoogle && (
        <p style={{ fontSize: '0.7rem', color: '#16a34a', marginTop: 0, marginBottom: '0.5rem' }}>
          Google token configured.
        </p>
      )}

      <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
        <input
          type="text"
          value={googleToken}
          onChange={(e) => { setGoogleToken(extractToken(e.target.value)); setError(null) }}
          placeholder="Google token (or paste the whole meta tag)"
          spellCheck={false}
          autoCapitalize="off"
          autoCorrect="off"
          style={{ padding: '0.4rem 0.55rem', border: '1px solid #ccc', borderRadius: '4px', fontSize: '0.8rem', fontFamily: 'ui-monospace, Menlo, monospace' }}
        />
        <input
          type="text"
          value={bingToken}
          onChange={(e) => { setBingToken(extractToken(e.target.value)); setError(null) }}
          placeholder="Bing token (optional)"
          spellCheck={false}
          autoCapitalize="off"
          autoCorrect="off"
          style={{ padding: '0.4rem 0.55rem', border: '1px solid #ccc', borderRadius: '4px', fontSize: '0.8rem', fontFamily: 'ui-monospace, Menlo, monospace' }}
        />

        {error && (
          <div style={{ padding: '0.4rem 0.5rem', background: '#fef2f2', border: '1px solid #fecaca', borderRadius: '4px' }}>
            <p style={{ fontSize: '0.75rem', color: '#dc3545', margin: 0 }}>{error}</p>
          </div>
        )}
        {success && (
          <div style={{ padding: '0.4rem 0.5rem', background: '#f0fdf4', border: '1px solid #bbf7d0', borderRadius: '4px' }}>
            <p style={{ fontSize: '0.75rem', color: '#166534', margin: 0 }}>{success}</p>
          </div>
        )}

        <button
          type="submit"
          disabled={mutation.isPending || (googleToken === savedGoogle && bingToken === savedBing)}
          style={{ fontSize: '0.8rem', padding: '0.4rem 0.75rem', alignSelf: 'flex-start' }}
        >
          {mutation.isPending ? 'Saving...' : 'Save tokens'}
        </button>
      </form>
    </div>
  )
}

function VendorFeedKeyCard({ vendorId }: { vendorId: string }) {
  const [name, setName] = useState('')
  const [newKey, setNewKey] = useState<string | null>(null)
  const [error, setError] = useState('')

  const createMutation = useMutation({
    mutationFn: (data: { name: string }) => api.vendors.createFeedApiKey(vendorId, data),
    onSuccess: (response: any) => {
      setNewKey(response?.data?.data?.raw_key)
      setName('')
      setError('')
    },
    onError: (err: any) => {
      setError(err.response?.data?.error?.message || err.response?.data?.message || 'Failed to create key')
      setNewKey(null)
    },
  })

  return (
    <div className="card">
      <h3 style={{ marginTop: 0 }}>Feed API Key</h3>
      <p style={{ fontSize: '0.8rem', color: '#666', marginBottom: '0.75rem' }}>
        Generate an OpenAI feed key scoped to this vendor.
      </p>

      {newKey && (
        <div style={{ padding: '0.5rem', background: '#f0fdf4', border: '1px solid #bbf7d0', borderRadius: '4px', marginBottom: '0.75rem' }}>
          <p style={{ fontSize: '0.7rem', color: '#666', margin: '0 0 0.25rem' }}>Copy now. Shown once only.</p>
          <code style={{ fontSize: '0.75rem', wordBreak: 'break-all' }}>{newKey}</code>
        </div>
      )}

      {error && (
        <div style={{ padding: '0.5rem', background: '#fef2f2', border: '1px solid #fecaca', borderRadius: '4px', marginBottom: '0.75rem' }}>
          <p style={{ fontSize: '0.8rem', color: '#dc3545', margin: 0 }}>{error}</p>
        </div>
      )}

      <div style={{ display: 'flex', gap: '0.5rem' }}>
        <input
          value={name}
          onChange={(e) => setName(e.target.value)}
          placeholder="Key name"
          style={{ flex: 1, padding: '0.4rem 0.5rem', border: '1px solid #ccc', borderRadius: '4px', fontSize: '0.85rem' }}
        />
        <button
          onClick={() => name.trim() && createMutation.mutate({ name: name.trim() })}
          disabled={createMutation.isPending || !name.trim()}
          style={{ fontSize: '0.8rem', padding: '0.4rem 0.75rem' }}
        >
          {createMutation.isPending ? '...' : 'Create'}
        </button>
      </div>
    </div>
  )
}

// ─── Platform API Keys (Admin) ──────────────────────────────────────────────

const API_KEY_GROUPS: Array<{ id: string; label: string; description: string }> = [
  { id: 'content', label: 'Content', description: 'Marketing blog create/edit/delete (Content MCP)' },
  { id: 'mcp', label: 'MCP', description: 'Travel MCP / AI agent booking tools' },
  { id: 'openai', label: 'OpenAI & AI Feed', description: 'Product feed and experience APIs' },
  { id: 'storefront', label: 'Storefront', description: 'Storefront and checkout APIs' },
  { id: 'widget', label: 'Widget', description: 'Read-only website widget' },
  { id: 'ai_booking_agent', label: 'AI Booking Agent', description: 'Vendor booking-agent chat APIs' },
]

function AdminApiKeys() {
  const qc = useQueryClient()
  const [showCreate, setShowCreate] = useState(false)
  const [rawKey, setRawKey] = useState<string | null>(null)
  const [copied, setCopied] = useState(false)
  const [formError, setFormError] = useState('')
  const [page, setPage] = useState(1)
  const [statusFilter, setStatusFilter] = useState<string>('active')
  const [form, setForm] = useState({
    name: '',
    description: '',
    api_groups: ['content'] as string[],
  })

  const { data, isLoading } = useQuery({
    queryKey: ['admin-api-keys', statusFilter],
    queryFn: () => api.admin.apiKeys.list(statusFilter ? { status: statusFilter } : undefined),
  })
  const keys: any[] = data?.data?.data?.keys || []

  const createMutation = useMutation({
    mutationFn: (d: { name: string; description?: string; api_groups: string[] }) =>
      api.admin.apiKeys.create(d),
    onSuccess: (res) => {
      qc.invalidateQueries({ queryKey: ['admin-api-keys'] })
      const created = res.data?.data
      setRawKey(created?.rawKey || null)
      setShowCreate(false)
      setForm({ name: '', description: '', api_groups: ['content'] })
      setFormError('')
    },
    onError: (err: any) => {
      setFormError(err.response?.data?.error?.message || err.response?.data?.message || 'Failed to create API key')
    },
  })

  const revokeMutation = useMutation({
    mutationFn: (keyId: string | number) => api.admin.apiKeys.revoke(String(keyId)),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['admin-api-keys'] }),
  })

  const toggleGroup = (groupId: string) => {
    setForm((prev) => {
      const has = prev.api_groups.includes(groupId)
      const next = has
        ? prev.api_groups.filter((g) => g !== groupId)
        : [...prev.api_groups, groupId]
      return { ...prev, api_groups: next }
    })
  }

  const copyRawKey = () => {
    if (!rawKey) return
    navigator.clipboard.writeText(rawKey)
    setCopied(true)
    setTimeout(() => setCopied(false), 2000)
  }

  if (isLoading) return <div><PageHeader title="API Keys" /><CardSkeleton /></div>

  return (
    <div>
      <PageHeader
        title="API Keys"
        action={
          <button
            onClick={() => {
              setShowCreate(!showCreate)
              setRawKey(null)
              setFormError('')
            }}
          >
            New API Key
          </button>
        }
      />

      <div className="card" style={{ marginBottom: '1.5rem', padding: '0.75rem 1rem' }}>
        <p style={{ fontSize: '0.85rem', color: '#666', margin: 0 }}>
          Platform API keys for integrations and Content MCP. For blog authoring, create a key with the{' '}
          <strong>content</strong> group and no vendor scope. The full key is shown once at creation.
        </p>
      </div>

      {rawKey && (
        <div className="card" style={{ marginBottom: '1.5rem', border: '2px solid #dc3545' }}>
          <p style={{ fontSize: '0.85rem', color: '#dc3545', fontWeight: 600, marginBottom: '1rem' }}>
            Copy this key now. It will not be shown again.
          </p>
          <label style={{ display: 'block', fontSize: '0.75rem', color: '#666', marginBottom: '0.125rem' }}>
            API Key (x-api-key / TIXXLY_CONTENT_API_KEY)
          </label>
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', padding: '0.375rem 0.5rem', background: '#f9fafb', borderRadius: '4px', border: '1px solid #e5e7eb' }}>
            <code style={{ flex: 1, fontSize: '0.75rem', wordBreak: 'break-all' }}>{rawKey}</code>
            <button
              type="button"
              onClick={copyRawKey}
              style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: '0.7rem', color: copied ? '#22c55e' : '#999' }}
            >
              {copied ? 'Copied' : 'Copy'}
            </button>
          </div>
          <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: '1rem' }}>
            <button type="button" onClick={() => setRawKey(null)}>Done</button>
          </div>
        </div>
      )}

      {showCreate && (
        <div className="card" style={{ marginBottom: '1.5rem' }}>
          <h3 style={{ marginTop: 0 }}>New platform API key</h3>
          {formError && <div style={{ color: '#dc3545', marginBottom: '1rem' }}>{formError}</div>}
          <div>
            <label style={{ display: 'block', marginBottom: '0.25rem', fontSize: '0.85rem' }}>Name *</label>
            <input
              value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
              placeholder="e.g. Marketing content MCP"
              style={{ width: '100%', padding: '0.5rem', border: '1px solid #ccc', borderRadius: '4px' }}
            />
          </div>
          <div style={{ marginTop: '0.75rem' }}>
            <label style={{ display: 'block', marginBottom: '0.25rem', fontSize: '0.85rem' }}>Description</label>
            <input
              value={form.description}
              onChange={(e) => setForm({ ...form, description: e.target.value })}
              placeholder="Optional"
              style={{ width: '100%', padding: '0.5rem', border: '1px solid #ccc', borderRadius: '4px' }}
            />
          </div>
          <div style={{ marginTop: '0.75rem' }}>
            <label style={{ display: 'block', marginBottom: '0.5rem', fontSize: '0.85rem' }}>API groups *</label>
            <div style={{ display: 'grid', gap: '0.5rem' }}>
              {API_KEY_GROUPS.map((group) => (
                <label
                  key={group.id}
                  style={{
                    display: 'flex',
                    gap: '0.5rem',
                    alignItems: 'flex-start',
                    padding: '0.5rem 0.75rem',
                    border: '1px solid #e5e7eb',
                    borderRadius: '6px',
                    cursor: 'pointer',
                    background: form.api_groups.includes(group.id) ? 'rgba(152,202,63,0.12)' : '#fff',
                  }}
                >
                  <input
                    type="checkbox"
                    checked={form.api_groups.includes(group.id)}
                    onChange={() => toggleGroup(group.id)}
                    style={{ marginTop: '0.2rem' }}
                  />
                  <span>
                    <strong style={{ fontSize: '0.85rem' }}>{group.label}</strong>
                    <span style={{ display: 'block', fontSize: '0.75rem', color: '#666' }}>{group.description}</span>
                  </span>
                </label>
              ))}
            </div>
          </div>
          <div style={{ display: 'flex', gap: '0.75rem', marginTop: '1rem' }}>
            <button
              type="button"
              onClick={() =>
                createMutation.mutate({
                  name: form.name.trim(),
                  description: form.description.trim() || undefined,
                  api_groups: form.api_groups,
                })
              }
              disabled={createMutation.isPending || !form.name.trim() || form.api_groups.length === 0}
            >
              {createMutation.isPending ? 'Creating...' : 'Create key'}
            </button>
            <button type="button" onClick={() => setShowCreate(false)} style={{ background: '#e5e7eb', color: '#333' }}>
              Cancel
            </button>
          </div>
        </div>
      )}

      <div style={{ marginBottom: '1rem', display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
        <label style={{ fontSize: '0.85rem', color: '#666' }}>Status</label>
        <select
          value={statusFilter}
          onChange={(e) => {
            setStatusFilter(e.target.value)
            setPage(1)
          }}
          style={{ padding: '0.35rem 0.5rem', border: '1px solid #ccc', borderRadius: '4px' }}
        >
          <option value="active">Active</option>
          <option value="revoked">Revoked</option>
          <option value="">All</option>
        </select>
      </div>

      <div style={{ overflowX: 'auto' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: '700px' }}>
          <thead>
            <tr style={{ borderBottom: '2px solid #e5e7eb', textAlign: 'left' }}>
              <th style={{ padding: '0.75rem' }}>Name</th>
              <th style={{ padding: '0.75rem' }}>Prefix</th>
              <th style={{ padding: '0.75rem' }}>Groups</th>
              <th style={{ padding: '0.75rem' }}>Vendor</th>
              <th style={{ padding: '0.75rem' }}>Status</th>
              <th style={{ padding: '0.75rem' }}>Created</th>
              <th style={{ padding: '0.75rem' }}>Actions</th>
            </tr>
          </thead>
          <tbody>
            {paginate(keys, page).map((k: any) => (
              <tr key={k.api_key_id} style={{ borderBottom: '1px solid #f3f4f6' }}>
                <td style={{ padding: '0.75rem', fontWeight: 500 }}>
                  {k.name}
                  {k.description ? (
                    <div style={{ fontSize: '0.75rem', color: '#888', fontWeight: 400 }}>{k.description}</div>
                  ) : null}
                </td>
                <td style={{ padding: '0.75rem' }}><code style={{ fontSize: '0.8rem' }}>{k.key_prefix}…</code></td>
                <td style={{ padding: '0.75rem' }}>
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.25rem' }}>
                    {(k.api_groups || []).map((g: string) => (
                      <span
                        key={g}
                        style={{
                          display: 'inline-block',
                          padding: '0.125rem 0.5rem',
                          background: g === 'content' ? 'rgba(152,202,63,0.2)' : '#eef2ff',
                          borderRadius: '4px',
                          fontSize: '0.75rem',
                        }}
                      >
                        {g}
                      </span>
                    ))}
                  </div>
                </td>
                <td style={{ padding: '0.75rem', fontSize: '0.85rem', color: '#666' }}>
                  {k.vendor_id ? `#${k.vendor_id}` : 'Platform'}
                </td>
                <td style={{ padding: '0.75rem' }}><StatusBadge status={k.status} /></td>
                <td style={{ padding: '0.75rem', fontSize: '0.85rem', color: '#666' }}>
                  {k.created_at ? new Date(k.created_at).toLocaleDateString() : '-'}
                </td>
                <td style={{ padding: '0.75rem' }}>
                  {k.status === 'active' && (
                    <button
                      type="button"
                      onClick={() => {
                        if (confirm(`Revoke "${k.name}"? This cannot be undone.`)) {
                          revokeMutation.mutate(k.api_key_id)
                        }
                      }}
                      style={{ fontSize: '0.8rem', padding: '0.25rem 0.75rem', background: '#dc3545', color: '#fff', border: 'none', borderRadius: '4px', cursor: 'pointer' }}
                    >
                      Revoke
                    </button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {keys.length === 0 && <p style={{ textAlign: 'center', color: '#666', marginTop: '2rem' }}>No API keys yet.</p>}
      <Pagination page={page} totalItems={keys.length} onPageChange={setPage} />
    </div>
  )
}

// ─── Router ─────────────────────────────────────────────────────────────────

export default function AdminDashboardPage() {
  return (
    <div className="container">
      <Routes>
        <Route index element={<AdminOverview />} />
        <Route path="vendors" element={<AdminVendors />} />
        <Route path="vendors/:vendorId" element={<AdminVendorDetail />} />
        <Route path="users" element={<AdminUsers />} />
        <Route path="plans" element={<Navigate to="/admin/pricing-tiers" replace />} />
        <Route path="pricing-tiers" element={<AdminPricingTiersPage />} />
        <Route path="features" element={<AdminFeatures />} />
        <Route path="fees" element={<AdminFees />} />
        <Route path="providers" element={<AdminProviders />} />
        <Route path="api-keys" element={<AdminApiKeys />} />
        <Route path="ai-connections" element={<AdminAiConnections />} />
      </Routes>
    </div>
  )
}
